

/* eslint-disable react/prop-types */
"use client";

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useNativeSwipeBack from "../../hooks/useNativeSwipeBack";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import JoinSection, { shouldUseParticipantRequestFlow } from "./JoinSection";
import HostMoreActionsPanel, {
  usesHostPaymentRequest,
  isPaidEventHostTransferUnavailable,
} from "./HostMoreActionsPanel";
import HostRewardBlock from "./HostRewardBlock";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import TranslatableEventDescription from "./TranslatableEventDescription";
import ParticipantTicketPanel, {
  hasPaidParticipantReservation,
} from "./ParticipantTicketPanel";
import EventBuddyPanel from "./EventBuddyPanel";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { BADGES } from "../../constants/badges";
import EventMetaIcon from "./EventMetaIcon";
import { buildEventPaymentSummary, isEventTicketPresentation } from "./eventPaymentSummary";
import WrappedSeparatedText from "./WrappedSeparatedText";
import { DELETED_USER_LABEL, isDeletedAccountUser } from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import {
  buildImageGradientPalette,
  rgbaToCss,
} from "../../utils/imageGradientPalette";
import { formatPhysicalEventLocation } from "./eventLocation";
import { isConceptAwaitingDetails } from "./conceptPresentation";
import { useImageGradientPalette } from "../../hooks/useImageGradientPalette";
import EventDetailsLoadingSkeleton from "./EventDetailsLoadingSkeleton";
import SinglesPartyAttendanceSummary from "./SinglesPartyAttendanceSummary";
import EventPromoGallerySection from "./EventPromoGallerySection";
import { prioritizePremiumParticipants } from "./ParticipantsSection";
import { getEventWeekdayAbbr } from "../../utils/eventDateLabels";
import { getEventSkillLevelPresentation } from "./eventSkillLevelMeta";
import { canJoinEventSession, isParticipantOnlyViewer } from "./eventRoleVisibility";
import { formatEventMoney, getEventCurrency } from "../../utils/eventCurrency";
import ValidationPendingNotice from "./ValidationPendingNotice";
import ChatNotificationBellIcon from "../menu/ChatNotificationBellIcon";
import "./EventDetailsActionDock.css";

// ──────────────────────────────────────────────────────────
// Adaptive two-column action grid for mobile
// Keep pairs together; labels can wrap inside their padded buttons.
// ──────────────────────────────────────────────────────────
export const AdaptiveActionGrid = ({
  items = [],
  gap = "gap-[14px]",
  forceTwoColumns = false,
  firstColumnWide = false,
}) => {
  const rows = useMemo(() => {
    if (forceTwoColumns) {
      const fixedRows = [];
      for (let i = 0; i < items.length; i += 2) {
        fixedRows.push(items.slice(i, i + 2));
      }
      return fixedRows;
    }

    const out = [];
    let i = 0;
    while (i < items.length) {
      const cur = items[i];
      const curWide = !!cur.forceFull;
      if (curWide) {
        out.push([cur]);
        i += 1;
        continue;
      }

      const next = items[i + 1];
      const nextWide = next ? !!next.forceFull : false;

      if (next && !nextWide) {
        out.push([cur, next]);
        i += 2;
      } else {
        // If the next one is wide (or missing), keep this one full-width
        out.push([cur]);
        i += 1;
      }
    }
    if (out.length > 3 && items.length <= 6) {
      const compactRows = [];
      for (let index = 0; index < items.length; index += 2) {
        compactRows.push(items.slice(index, index + 2));
      }
      return compactRows;
    }
    return out;
  }, [forceTwoColumns, items]);

  const baseBtn = "flex min-h-[55px] gap-2 justify-center items-center rounded-full px-3 py-2 text-lg font-semibold";
  const renderUnreadBadge = (count) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    const label = n > 999 ? "999+" : String(n);
    return (
      <span
        data-testid="action-unread-badge"
        className="pointer-events-none absolute right-4 top-1.5 z-20 bg-white text-blue-600 font-extrabold leading-none h-[18px] min-w-[18px] px-[5px] rounded-full ring-2 ring-blue-500 text-[11px] flex items-center justify-center shadow-[0_0_8px_rgba(255,255,255,0.45)]"
        aria-hidden="true"
      >
        {label}
      </span>
    );
  };

  return (
    <div className={`flex flex-col ${gap}`}>
      {rows.map((row, idx) => (
        <div
          key={idx}
          className={
            row.length === 2 && forceTwoColumns
              ? `grid ${
                  firstColumnWide
                    ? "grid-cols-[minmax(0,1.16fr)_minmax(0,0.84fr)]"
                    : "grid-cols-[minmax(0,0.84fr)_minmax(0,1.16fr)]"
                } ${gap}`
              : `flex ${row.length === 2 ? `justify-between ${gap}` : ""}`
          }
        >
          {row.map((it) => {
            const isHalf = row.length === 2;
            const widthClass = isHalf
              ? forceTwoColumns
                ? "min-w-0 w-full"
                : "min-w-0 flex-1"
              : "w-full";

            return (
              <button
                key={it.id}
                data-event-action-fit="true"
                type="button"
                aria-label={it.ariaLabel}
                disabled={!!it.disabled}
                onClick={it.onClick}
                className={`relative ${baseBtn} ${widthClass} ${it.className || ""} ${it.disabled ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {renderUnreadBadge(it.badgeCount)}
                {it.iconSrc ? (
                  <img
                    src={it.iconSrc}
                    alt={it.iconAlt || ""}
                    className={`shrink-0 ${it.iconClassName || "w-6 h-6"}`}
                  />
                ) : null}
                <span
                  data-btn-label
                  className="min-w-0 text-center leading-[1.2] [overflow-wrap:anywhere]"
                >
                  {it.label}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export const fitEventActionFont = (dock) => {
  if (!dock || typeof window === "undefined") return;
  const buttons = Array.from(dock.querySelectorAll("[data-event-action-fit]"));
  if (!buttons.length) return;

  for (let size = 18; size >= 12; size -= 1) {
    dock.style.setProperty("--event-action-font-size", `${size}px`);
    const fits = buttons.every((button) => {
      const label = button.querySelector("[data-btn-label]");
      if (!label) return true;
      const buttonRect = button.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      if (!buttonRect.width || !labelRect.width) return true;
      const computedLineHeight = parseFloat(window.getComputedStyle(label).lineHeight);
      const lineHeight = computedLineHeight > 5 ? computedLineHeight : size * 1.2;
      const hasWrap = labelRect.height > lineHeight * 1.35;
      const hasHorizontalOverflow =
        label.scrollWidth > label.clientWidth + 1 ||
        labelRect.left < buttonRect.left + 10 ||
        labelRect.right > buttonRect.right - 10;
      return !hasWrap && !hasHorizontalOverflow;
    });
    if (fits) break;
  }
};

export const resolveActionDockNavigationInsetPx = (containerRect, navigationRect) => {
  const containerBottom = Number(containerRect?.bottom);
  const navigationTop = Number(navigationRect?.top);
  const navigationWidth = Number(navigationRect?.width);
  const navigationHeight = Number(navigationRect?.height);

  if (
    !Number.isFinite(containerBottom) ||
    !Number.isFinite(navigationTop) ||
    !Number.isFinite(navigationWidth) ||
    !Number.isFinite(navigationHeight) ||
    navigationWidth <= 0 ||
    navigationHeight <= 0
  ) {
    return null;
  }

  // The event shell and fixed navigation can use different viewport origins in
  // iOS Safari. Their live rects keep the dock aligned when browser chrome moves.
  return Math.max(0, Math.ceil(containerBottom - navigationTop));
};

const EVENT_CHAT_BUTTON_ICON_SRC = "/assets/events/event_chat.svg";
const DEFAULT_EVENT_AVATAR_SRC = "/assets/avatars/paper-cap.webp";
const DEFAULT_EVENT_PICTURE_SRC = "/assets/event_pics/default.webp";
const EVENT_HERO_FALLBACK_RGB = { r: 9, g: 20, b: 32 };
const EVENT_FALLBACK_GRADIENT_PALETTE = buildImageGradientPalette(
  EVENT_HERO_FALLBACK_RGB,
  EVENT_HERO_FALLBACK_RGB
);
const EVENT_DETAILS_DEFAULT_BACKGROUND_STYLE = {
  backgroundColor: "#050812",
  ...AUTH_GRADIENT_STYLE,
};

const EVENT_HERO_TUNE = {
  imageHeight: "19rem",
  overlayHeight: "52rem",
  transitionShift: "0rem",
  blurTop: "10rem",
  blurHeight: "12rem",
  fadeFeatherAt: "1.5rem",
  fadeLightAt: "4rem",
  fadeSoftAt: "8rem",
  fadeMediumAt: "13rem",
  fadeStrongAt: "18rem",
  fadeSolidAt: "25rem",
  fadeBlueAt: "32rem",
  fadeDeepAt: "40rem",
  fadeToPageAt: "50rem",
};
const EVENT_HERO_TOP_CROP_PX = 82;
const EVENT_HERO_BACKDROP_HEIGHT = `calc(${EVENT_HERO_TUNE.imageHeight} + 7rem)`;

const shiftEventHeroStop = (value) =>
  EVENT_HERO_TUNE.transitionShift === "0rem"
    ? value
    : `calc(${value} + ${EVENT_HERO_TUNE.transitionShift})`;

const buildEventHeroOverlay = (palette = EVENT_FALLBACK_GRADIENT_PALETTE) =>
  `linear-gradient(180deg,
    ${rgbaToCss(palette.shadow, 0.02)} 0rem,
    ${rgbaToCss(palette.shadow, 0.08)} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeFeatherAt)},
    ${rgbaToCss(palette.soft, 0.18)} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeLightAt)},
    ${rgbaToCss(palette.soft, 0.44)} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeSoftAt)},
    ${rgbaToCss(palette.base, 0.72)} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeMediumAt)},
    ${palette.softCss} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeStrongAt)},
    ${palette.baseCss} ${shiftEventHeroStop(EVENT_HERO_TUNE.fadeSolidAt)},
    ${palette.floorCss} 100%)`;

const EVENT_HERO_BLUR_MASK =
  "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.08) 10%, rgba(0,0,0,0.34) 24%, rgba(0,0,0,0.72) 43%, rgba(0,0,0,1) 58%, rgba(0,0,0,0.82) 73%, rgba(0,0,0,0.38) 87%, transparent 100%)";

export function useEventHeroGradientPalette(src) {
  return useImageGradientPalette(src, EVENT_HERO_FALLBACK_RGB).palette;
}

const EVENT_TOP_ICON_BUTTON_CLASS =
  "flex h-[36px] w-[36px] items-center justify-center rounded-full border border-white/20 bg-white/15 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12),inset_0_1px_9px_rgba(255,255,255,0.24),0_8px_18px_rgba(5,10,28,0.18)] backdrop-blur-sm";
const EVENT_DETAILS_PRIMARY_CTA_CLASS =
  "flex h-[55px] w-full min-w-0 items-center justify-center rounded-[100px] border border-white/20 bg-white px-5 text-center text-lg font-semibold leading-6 text-blue-600 shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition active:scale-[0.98]";
const EVENT_DETAILS_DISABLED_CTA_CLASS =
  "flex h-[55px] w-full min-w-0 cursor-not-allowed items-center justify-center rounded-[100px] border border-white/15 bg-white/50 px-5 text-center text-lg font-semibold leading-6 text-blue-600 opacity-60";
const EVENT_DETAILS_SECONDARY_CTA_CLASS =
  "flex h-[55px] w-[70px] shrink-0 items-center justify-center rounded-[100px] border border-white/20 bg-white text-[#3E6FFB] shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition active:scale-[0.98]";

const pad2 = (value) => String(value).padStart(2, "0");

const JoinPlusIcon = ({ className = "" }) => (
  <span
    aria-hidden="true"
    className={`mr-[8px] inline-flex h-[24px] w-[18px] translate-y-[-1px] items-center justify-center text-[30px] font-light leading-none ${className}`}
  >
    +
  </span>
);

const getPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const getEventPaymentTotal = (ev) => {
  const categoryTotal = [
    ev?.payment_category_1,
    ev?.payment_category_2,
    ev?.payment_category_3,
    ev?.payment_category_4,
  ].reduce((sum, value) => sum + getPositiveNumber(value), 0);
  if (categoryTotal > 0) return categoryTotal;

  const fallbackPrices = [
    ev?.ticket_price,
    ev?.price,
    ev?.fee_amount,
    ev?.male_ticket_price,
    ev?.female_ticket_price,
    ev?.multisport_ticket_price,
  ]
    .map(getPositiveNumber)
    .filter(Boolean);

  return fallbackPrices.length ? Math.max(...fallbackPrices) : 0;
};

const getEventCoverSrc = (ev) => String(ev?.hero_image_url || "").trim();

const getBadgeSrc = (label) => {
  const badge = BADGES.find((item) => item.id === label);
  return badge?.src || DEFAULT_EVENT_PICTURE_SRC;
};

const getEventSummaryImageSrc = ({ eventIconSrc, badgeLabel }) => {
  if (eventIconSrc) return eventIconSrc;
  return getBadgeSrc(badgeLabel);
};

const isBusinessHostEvent = (ev) => {
  const hostFormatNumber = Number(ev?.host_format);
  return Number.isFinite(hostFormatNumber) && hostFormatNumber !== 0;
};

const parseEventDate = (ev, fallback) => {
  const raw = ev?.date_time || ev?.datetime || ev?.start_time || fallback;
  if (!raw) return null;
  const parsed = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
};

export const formatEventDateTimeLabel = (ev, fallback, t) => {
  if (ev?.type === "concept") {
    return t("events.details.concept_datetime_note", {
      defaultValue: "Date and time will be set later",
    });
  }

  const date = parseEventDate(ev, fallback);
  if (!date) return "";
  const dateParts = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "long",
  }).formatToParts(date);
  const dayText = dateParts.find((part) => part.type === "day")?.value || "";
  const monthText = dateParts.find((part) => part.type === "month")?.value || "";
  const dateText = [dayText, monthText].filter(Boolean).join(" ");
  const timeText = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return t("event_description_modal.meta.date_at_time", {
    date: dateText,
    time: timeText,
    defaultValue: `${dateText} at ${timeText}`,
  });
};

export const formatEventCardDateTimeLabel = (ev, fallback, language) => {
  const date = parseEventDate(ev, fallback);
  if (!date) return String(fallback || "");
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())} ${pad2(date.getDate())}/${pad2(
    date.getMonth() + 1
  )} ${getEventWeekdayAbbr(date.getDay(), language)}`;
};

export const formatDurationLabel = (minutes, t) => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total <= 0) return "";
  if (total < 60) {
    return t("event_description_modal.meta.duration_minutes", {
      minutes: Math.round(total),
      defaultValue: `About ${Math.round(total)} min`,
    });
  }

  const hours = Math.round(total / 60);
  if (hours === 1) {
    return t("event_description_modal.meta.duration_hour", {
      defaultValue: "About 1 hour",
    });
  }
  return t("event_description_modal.meta.duration_hours", {
    count: hours,
    defaultValue: `About ${hours} hours`,
  });
};

export const getLocationLabel = (ev, t, language = "en") => {
  if (ev?.exact_venue_hidden) {
    return t("join_section.singles_party.venue_hidden");
  }
  if (Number(ev?.event_format) === 0) {
    return t("events.details.online_label", { defaultValue: "Online" });
  }

  return formatPhysicalEventLocation(ev, t("events.details.show_on_map"), language);
};

const MAX_CONDITION_CHIPS = 3;
const CONDITION_CHIP_GAP_PX = 6;
const CONDITION_CHIP_CLASS_NAME =
  "inline-flex min-h-[35px] min-w-[86px] shrink-0 items-center justify-center gap-[6px] whitespace-nowrap rounded-full bg-white/20 px-[14px] text-[13px] font-bold leading-[18px] text-white";
const SOCIAL_FIT_CHIP_CLASS_BY_STATUS = {
  strong: "bg-emerald-300/92 text-[#072116]",
  good: "bg-amber-200/92 text-[#2a1d05]",
  low: "bg-orange-300/92 text-[#2b1200]",
  outside: "bg-rose-200/92 text-[#2a0710]",
  learning: "bg-sky-200/92 text-[#10213d]",
};

const getConditionChipPreview = (chips) => chips.slice(0, MAX_CONDITION_CHIPS);

const buildSocialPreferenceFitChip = (ev, t) => {
  const fit = ev?.social_preference_fit || null;
  if (fit?.is_within_audience === false) return null;
  const status = String(fit?.status || "").trim().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(SOCIAL_FIT_CHIP_CLASS_BY_STATUS, status)) {
    return null;
  }
  return {
    id: "social-preference-fit",
    key: `social-preference-fit-${status}`,
    label: t(`event_description_modal.chips.social_fit.${status}`, {
      defaultValue: fit?.label || "Social Preference Fit",
    }),
    className: SOCIAL_FIT_CHIP_CLASS_BY_STATUS[status],
  };
};

export const buildConditionChips = ({ ev, t, targetAgeLabel }) => {
  const socialFitChip = buildSocialPreferenceFitChip(ev, t);
  if (socialFitChip) {
    return [socialFitChip];
  }

  const chips = [];
  const pushUnique = (chip) => {
    const label = String(chip?.label || "").trim();
    if (!label) return;
    const key = `${chip?.id || label}:${label}`;
    if (chips.some((item) => item.key === key)) return;
    chips.push({ ...chip, key, label });
  };
  const numericFeeType = Number(ev?.fee_type ?? 0);
  const amount = getEventPaymentTotal(ev);
  const amountText = formatEventMoney(amount, ev);
  const isTicket = isEventTicketPresentation(ev);
  const isOnline = Number(ev?.event_format) === 0;
  const awaitingConceptDetails = isConceptAwaitingDetails(ev);

  if (numericFeeType === 0 && amount <= 0) {
    if (!awaitingConceptDetails) {
      pushUnique({
        id: "cost",
        label: t("event_description_modal.chips.free", { defaultValue: "Free" }),
        iconSrc: "/assets/events/payable_icon.svg",
      });
    }
  } else if (isTicket) {
    pushUnique({
      id: "cost",
      label: amountText
        ? t("event_description_modal.chips.ticket_with_amount", {
            amount: amountText,
            defaultValue: `Ticket: ${amountText}`,
          })
        : t("event_description_modal.chips.ticket", { defaultValue: "Ticket" }),
      iconSrc: "/assets/events/payable_icon.svg",
    });
  } else {
    pushUnique({
      id: "cost",
      label: amountText
        ? t("event_description_modal.chips.shared_cost_with_amount", {
            amount: amountText,
            defaultValue: `Shared Cost: ${amountText}`,
          })
        : t("event_description_modal.chips.shared_cost", { defaultValue: "Shared Cost" }),
      iconSrc: "/assets/events/payable_icon.svg",
    });
  }

  if (!awaitingConceptDetails) {
    pushUnique({
      id: "format",
      label: isOnline
        ? t("events.details.online", { defaultValue: "Online" })
        : t("events.details.offline", { defaultValue: "Offline" }),
      iconSrc: isOnline ? "/assets/events/online_icon.svg" : "/assets/events/offline_icon.svg",
    });
  }

  const requiredSkillLevel = getEventSkillLevelPresentation(ev?.required_skill_level, t);
  if (requiredSkillLevel) {
    pushUnique({
      id: "skill-level",
      label: requiredSkillLevel.label,
      iconSrc: requiredSkillLevel.iconSrc,
    });
  }

  const targetGender = String(ev?.target_gender || "").toLowerCase();
  if (targetGender === "female") {
    pushUnique({
      id: "gender",
      label: t("event_description_modal.chips.female_only", { defaultValue: "Female only" }),
      iconSrc: "/assets/events/female_event.svg",
    });
  } else if (targetGender === "male") {
    pushUnique({
      id: "gender",
      label: t("event_description_modal.chips.male_only", { defaultValue: "Male only" }),
      iconSrc: "/assets/events/male_event.svg",
    });
  }

  if (targetAgeLabel) {
    pushUnique({
      id: "age",
      label: t("event_description_modal.chips.age", {
        age: targetAgeLabel,
        defaultValue: `Age ${targetAgeLabel}`,
      }),
      iconSrc: "/assets/events/age_icon.svg",
    });
  }
  if (awaitingConceptDetails && !targetAgeLabel) {
    pushUnique({
      id: "age",
      label: t("discovery_filters.any_age", { defaultValue: "Any age" }),
      iconSrc: "/assets/events/age_icon.svg",
    });
  }

  pushUnique({
    id: "host-kind",
    label: isBusinessHostEvent(ev)
      ? t("event_description_modal.chips.club_host_short", { defaultValue: "Club" })
      : t("event_description_modal.chips.personal_host_short", { defaultValue: "Host" }),
    iconSrc: isBusinessHostEvent(ev)
      ? "/assets/events/organization_icon.svg"
      : "/assets/events/personal_icon.svg",
  });

  if (ev?.is_verified) {
    pushUnique({
      id: "verified",
      label: t("events.details.official_event", { defaultValue: "Verified" }),
      iconSrc: "/assets/events/verified_icon.svg",
    });
  }

  return getConditionChipPreview(chips);
};

export const EventDetailsSummaryCard = ({
  ev,
  imageSrc,
  dateLabel,
  isFavourite,
  onToggleFavourite,
  t,
}) => {
  const handleFavouriteClick = (event) => {
    event.stopPropagation();
    onToggleFavourite?.(event);
  };
  const isOnline = Number(ev?.event_format) === 0;
  const isPaid =
    Number(ev?.fee_type ?? 0) !== 0 &&
    (getEventPaymentTotal(ev) > 0 || String(ev?.pricing_model || "").toLowerCase() !== "free");
  const awaitingConceptDetails = isConceptAwaitingDetails(ev);
  const summaryMetaIcons = [
    ...(!awaitingConceptDetails ? [{
      id: "format",
      src: isOnline ? "/assets/events/online_icon.svg" : "/assets/events/offline_icon.svg",
      alt: isOnline
        ? t("event_card.icons.online_alt")
        : t("event_card.icons.offline_alt"),
    }] : []),
    {
      id: "host-kind",
      src: isBusinessHostEvent(ev)
        ? "/assets/events/organization_icon.svg"
        : "/assets/events/personal_icon.svg",
      alt: isBusinessHostEvent(ev)
        ? t("event_card.icons.organization_alt")
        : t("event_card.icons.personal_alt"),
    },
    ...(isPaid
      ? [
          {
            id: "paid",
            src: "/assets/events/payable_icon.svg",
            alt: t("event_card.icons.paid_alt"),
          },
        ]
      : []),
    ...(ev?.is_verified
      ? [
          {
            id: "verified",
            src: "/assets/events/verified_icon.svg",
            alt: t("event_card.icons.verified_alt"),
          },
        ]
      : []),
    ...(ev?.join_by_request ||
    (Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0)
      ? [
          {
            id: "private",
            src: "/assets/events/private_event.svg",
            alt: t("event_card.icons.by_request_alt"),
          },
        ]
      : []),
  ];
  const hasCrowdedMetaIcons = summaryMetaIcons.length > 4;

  return (
    <article className="relative mx-auto h-[75px] w-full max-w-[350px] rounded-[16px] border border-white/25 bg-[linear-gradient(180deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0)_100%)]">
      <img
        src={imageSrc}
        alt={t("event_card.thumbnails.avatar_alt", { title: ev?.name || "" })}
        onError={(event) => {
          event.currentTarget.src = DEFAULT_EVENT_PICTURE_SRC;
        }}
        className="absolute left-0 top-0 h-[75px] w-[75px] rounded-[16px] border border-white object-cover"
      />
      <h3 className="absolute left-[85px] right-[44px] top-[11px] truncate text-[18px] font-semibold leading-[22px] text-white">
        {ev?.name}
      </h3>
      <button
        type="button"
        onClick={handleFavouriteClick}
        className="absolute right-[14px] top-[13px] flex h-[19px] w-[19px] items-center justify-center"
        aria-label={t("event_card.actions.favourite_aria")}
      >
        <img
          src={`/assets/events/${isFavourite ? "like_icon_on.svg" : "like_icon_off.svg"}`}
          alt=""
          aria-hidden="true"
          className="h-[19px] w-[19px]"
        />
      </button>
      <div
        className={`absolute left-[85px] top-[43px] flex h-[22px] items-center ${
          hasCrowdedMetaIcons ? "gap-[5px]" : "gap-[7px]"
        }`}
      >
        {summaryMetaIcons.map((icon) => (
          <EventMetaIcon
            key={icon.id}
            src={icon.src}
            alt={icon.alt}
            className={hasCrowdedMetaIcons ? "h-[20px] w-[20px]" : "h-[22px] w-[22px]"}
          />
        ))}
      </div>
      {dateLabel ? (
        <div data-newcomer-tour="date" className="absolute right-[15px] top-[43px] max-w-[130px] truncate text-right text-[14px] font-medium leading-[22px] text-white/50">
          {dateLabel}
        </div>
      ) : null}
    </article>
  );
};

const CalendarGlyph = ({ className = "h-[28px] w-[28px]" }) => (
  <svg
    viewBox="0 0 28 28"
    aria-hidden="true"
    className={className}
    fill="none"
  >
    <rect
      x="4"
      y="6.5"
      width="20"
      height="17.5"
      rx="2.7"
      stroke="currentColor"
      strokeWidth="2.4"
    />
    <path d="M8 4.5V9.5M20 4.5V9.5M5 12H23" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const EventHeroBackdrop = ({ coverSrc, title, palette = EVENT_FALLBACK_GRADIENT_PALETTE }) => {
  const hasCover = Boolean(coverSrc);
  if (!hasCover) return null;

  return (
    <>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 overflow-hidden"
        style={{ height: EVENT_HERO_BACKDROP_HEIGHT }}
      >
        <div
          className="absolute left-1/2 w-[calc(100%+14px)] -translate-x-1/2 overflow-hidden"
          style={{
            top: `-${EVENT_HERO_TOP_CROP_PX}px`,
            height: `calc(${EVENT_HERO_TUNE.imageHeight} + ${EVENT_HERO_TOP_CROP_PX}px)`,
          }}
        >
          <img
            src={coverSrc}
            alt={title || ""}
            className="h-full w-full object-cover"
          />
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 top-0 z-0"
        style={{
          minHeight: EVENT_HERO_TUNE.overlayHeight,
          backgroundImage: buildEventHeroOverlay(palette),
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-[-24px] z-0 overflow-hidden opacity-75"
        style={{
          top: shiftEventHeroStop(EVENT_HERO_TUNE.blurTop),
          height: EVENT_HERO_TUNE.blurHeight,
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          WebkitMaskImage: EVENT_HERO_BLUR_MASK,
          maskImage: EVENT_HERO_BLUR_MASK,
        }}
      />
    </>
  );
};

const EventConditionChip = React.forwardRef(({ chip }, ref) => {
  const isMultilineSocialFit = chip.id === "social-preference-fit" && chip.label.includes("\n");
  return (
  <span
    ref={ref}
    className={`${CONDITION_CHIP_CLASS_NAME} ${isMultilineSocialFit ? "w-[min(350px,calc(100vw-40px))] whitespace-pre-line py-2 text-center leading-[17px]" : ""} ${chip.className || ""}`}
  >
    {chip.iconSrc ? (
      <EventMetaIcon
        src={chip.iconSrc}
        alt=""
        decorative
        className="h-[17px] w-[17px]"
      />
    ) : null}
    <span>{chip.label}</span>
  </span>
  );
});
EventConditionChip.displayName = "EventConditionChip";

export const EventConditionChips = ({ chips }) => {
  const previewChips = useMemo(() => getConditionChipPreview(chips), [chips]);
  const containerRef = useRef(null);
  const measureRefs = useRef(new Map());
  const [visibleCount, setVisibleCount] = useState(previewChips.length);

  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      if (!container || !previewChips.length) return;

      const availableWidth = container.clientWidth;
      let usedWidth = 0;
      let nextVisibleCount = 0;

      for (const chip of previewChips) {
        const chipKey = chip.key || chip.id || chip.label;
        const chipElement = measureRefs.current.get(chipKey);
        const chipWidth = chipElement?.getBoundingClientRect?.().width || 0;
        if (!chipWidth) continue;

        const requiredWidth = chipWidth + (nextVisibleCount > 0 ? CONDITION_CHIP_GAP_PX : 0);
        if (usedWidth + requiredWidth > availableWidth + 0.5) break;

        usedWidth += requiredWidth;
        nextVisibleCount += 1;
      }

      setVisibleCount(Math.max(1, nextVisibleCount));
    };

    measure();

    const container = containerRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && container
        ? new ResizeObserver(measure)
        : null;
    resizeObserver?.observe(container);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [previewChips]);

  if (!previewChips.length) return null;

  const visibleChips = previewChips.slice(0, Math.min(visibleCount, previewChips.length));

  return (
    <div
      ref={containerRef}
      className="relative z-10 mt-[10px] flex w-full max-w-[350px] flex-nowrap justify-center gap-[6px] self-center px-0"
    >
      {visibleChips.map((chip) => (
        <EventConditionChip
          key={chip.key || chip.id || chip.label}
          chip={chip}
        />
      ))}
      <div
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 flex flex-nowrap gap-[6px]"
      >
        {previewChips.map((chip) => {
          const chipKey = chip.key || chip.id || chip.label;
          return (
            <EventConditionChip
              key={chipKey}
              chip={chip}
              ref={(node) => {
                if (node) {
                  measureRefs.current.set(chipKey, node);
                } else {
                  measureRefs.current.delete(chipKey);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const EventInfoRow = ({ icon, children }) => (
  <div className="grid grid-cols-[22px_minmax(0,1fr)] items-center gap-[8px] text-white">
    <div className="flex h-[20px] w-[20px] items-center justify-center text-white">
      {icon}
    </div>
    <div className="min-w-0 text-[15px] font-medium leading-[22px]">{children}</div>
  </div>
);

export const EventInfoRows = ({
  ev,
  t,
  locationLabel,
  durationLabel,
  dateLabel,
  paymentSummary,
  canAddToCalendar,
  onAddToCalendar,
  goToMap,
}) => {
  const awaitingConceptDetails = isConceptAwaitingDetails(ev);
  const canShowMap = Number(ev?.event_format) !== 0 && !awaitingConceptDetails;
  const isFreeEvent = String(ev?.pricing_model || "free").trim().toLowerCase() === "free";
  const targetGender = String(ev?.target_gender || "").trim().toLowerCase();
  const targetGenderLabel =
    targetGender === "female"
      ? t("event_description_modal.chips.female_only", { defaultValue: "Female only" })
      : targetGender === "male"
        ? t("event_description_modal.chips.male_only", { defaultValue: "Male only" })
        : "";
  const targetGenderIcon =
    targetGender === "female"
      ? "/assets/events/female_event.svg"
      : targetGender === "male"
        ? "/assets/events/male_event.svg"
        : "";
  const ageMin = Number(ev?.target_age_min);
  const ageMax = Number(ev?.target_age_max);
  const hasAgeMin = ev?.target_age_min !== null && ev?.target_age_min !== undefined && String(ev.target_age_min) !== "";
  const hasAgeMax = ev?.target_age_max !== null && ev?.target_age_max !== undefined && String(ev.target_age_max) !== "";
  const targetAgeLabel =
    hasAgeMin && hasAgeMax && Number.isFinite(ageMin) && Number.isFinite(ageMax)
      ? `${ageMin}-${ageMax}`
      : hasAgeMin && Number.isFinite(ageMin)
        ? `${ageMin}+`
        : hasAgeMax && Number.isFinite(ageMax)
          ? `≤${ageMax}`
          : "";
  const requiredSkillLevel = getEventSkillLevelPresentation(ev?.required_skill_level, t);
  const minVaultLevel = Number(ev?.min_vault_level) || 0;
  const eventLanguageCode = String(ev?.event_language || "").trim().toLowerCase();
  const eventLanguageLabel = eventLanguageCode
    ? t(`event_create.step_advanced.language.options.${eventLanguageCode}`, {
        defaultValue:
          String(ev?.event_language_label || "").trim() || eventLanguageCode.toUpperCase(),
      })
    : "";

  return (
    <section className="mt-[24px] flex flex-col gap-[7px]">
      {!awaitingConceptDetails && <EventInfoRow
        icon={
          <img
            src={ev?.event_format === 0 ? "/assets/events/online_icon.svg" : "/assets/events/map_pin.svg"}
            alt=""
            aria-hidden="true"
            className="h-[20px] w-[20px]"
          />
        }
      >
        <div className="flex min-w-0 items-center gap-[12px]">
          <span className="min-w-0 truncate">{locationLabel}</span>
          {canShowMap ? (
            <button
              type="button"
              onClick={goToMap}
              className="inline-flex h-[18px] min-w-max shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-white/30 px-[12px] text-[9px] font-medium leading-[16px] text-white/50"
            >
              {t("events.details.show_on_map")}
            </button>
          ) : null}
        </div>
      </EventInfoRow>}

      {durationLabel ? (
        <EventInfoRow
          icon={
            <img
              src="/assets/events/duration_icon.svg"
              alt=""
              aria-hidden="true"
              className="h-[18px] w-[18px]"
            />
          }
        >
          {durationLabel}
        </EventInfoRow>
      ) : null}

      {dateLabel ? (
        <EventInfoRow icon={<CalendarGlyph className="h-[20px] w-[20px]" />}>
          <div className="flex min-w-0 items-center gap-[12px]">
            <span className="min-w-0">{dateLabel}</span>
            {canAddToCalendar ? (
              <button
                type="button"
                onClick={onAddToCalendar}
                className="inline-flex h-[18px] min-w-[118px] shrink-0 items-center justify-center gap-[4px] whitespace-nowrap rounded-full border border-white/30 px-[11px] text-[9px] font-medium leading-[16px] text-white/50"
              >
                <span aria-hidden="true">+</span>
                <span>{t("events.details.join_chat.add_to_calendar")}</span>
              </button>
            ) : null}
          </div>
        </EventInfoRow>
      ) : null}

      {eventLanguageLabel ? (
        <EventInfoRow
          icon={
            <img
              src="/assets/events/language_icon.svg"
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          {eventLanguageLabel}
        </EventInfoRow>
      ) : null}

      {requiredSkillLevel ? (
        <EventInfoRow
          icon={
            <img
              src={requiredSkillLevel.iconSrc}
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          {requiredSkillLevel.label}
        </EventInfoRow>
      ) : null}

      {targetGenderLabel && targetGenderIcon ? (
        <EventInfoRow
          icon={
            <img
              src={targetGenderIcon}
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          {targetGenderLabel}
        </EventInfoRow>
      ) : null}

      {targetAgeLabel ? (
        <EventInfoRow
          icon={
            <img
              src="/assets/events/age_icon.svg"
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          {t("event_description_modal.chips.age", {
            age: targetAgeLabel,
            defaultValue: `Age ${targetAgeLabel}`,
          })}
        </EventInfoRow>
      ) : null}

      {awaitingConceptDetails && !targetAgeLabel ? (
        <EventInfoRow icon={<img src="/assets/events/age_icon.svg" alt="" aria-hidden="true" className="h-[20px] w-[20px]" />}>
          {t("discovery_filters.any_age", { defaultValue: "Any age" })}
        </EventInfoRow>
      ) : null}

      {minVaultLevel > (awaitingConceptDetails ? 1 : 0) ? (
        <EventInfoRow
          icon={<img src="/assets/events/logo-go.svg" alt="" aria-hidden="true" className="h-[20px] w-[20px]" />}
        >
          {t("events.details.min_vault_level", { level: minVaultLevel })}
        </EventInfoRow>
      ) : null}

      {paymentSummary ? (
        <EventInfoRow
          icon={
            <img
              src="/assets/events/fee_type.svg"
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          <div className="min-w-0 [overflow-wrap:anywhere]">
            <span>{paymentSummary.label}: </span>
            <span className="font-normal">
              {Array.isArray(paymentSummary.items) && paymentSummary.items.length ? (
                <WrappedSeparatedText items={paymentSummary.items} />
              ) : (
                paymentSummary.value
              )}
            </span>
          </div>
        </EventInfoRow>
      ) : null}

      {isFreeEvent && !awaitingConceptDetails ? (
        <EventInfoRow
          icon={
            <img
              src="/assets/events/Badge.svg"
              alt=""
              aria-hidden="true"
              className="h-[20px] w-[20px]"
            />
          }
        >
          {t("events.details.free_event", { defaultValue: "Free event" })}
        </EventInfoRow>
      ) : null}

    </section>
  );
};

const resolveParticipantAvatarSrc = (person) => {
  return resolveUserAvatarSrc(person, DEFAULT_EVENT_AVATAR_SRC);
};

export const EventPeopleStrip = ({
  t,
  participants = [],
  participantsCount = 0,
  currency = "EUR",
  maxParticipants,
  onOpenParticipant,
  loadingCode,
  userStorageCode,
  intentStatus,
  activationDateBadgesEnabled,
  showPaymentBadges,
  isHost,
  reserveUI = null,
  isFull = false,
  unlockBeforeLabel = null,
}) => {
  const total = Number(participantsCount) || participants.length || 0;
  const maxLabel = maxParticipants ? maxParticipants : "∞";
  const numericMaxParticipants = Number(maxParticipants);
  const hasNumericMaxParticipants =
    Number.isFinite(numericMaxParticipants) && numericMaxParticipants > 0;
  const lockedRemaining = Math.max(0, Math.floor(Number(reserveUI?.lockedRemaining) || 0));
  const reservedSlotsCount =
    reserveUI?.lockActive && lockedRemaining > 0 && !isFull
      ? Math.min(
          lockedRemaining,
          hasNumericMaxParticipants ? numericMaxParticipants : lockedRemaining
        )
      : 0;
  const hasReservedSlots = reservedSlotsCount > 0;
  const hasParticipantVisuals = participants.length > 0 || hasReservedSlots;
  const orderedParticipants = useMemo(
    () => prioritizePremiumParticipants(participants),
    [participants]
  );

  return (
    <section className="mt-[30px]">
      <h2 className="text-[32px] font-medium leading-[34px] text-white">
        {t("participants_section.title", { defaultValue: "Participants" })} ({total}/{maxLabel})
      </h2>
      {hasParticipantVisuals ? (
        <div className="-mx-[30px] mt-[12px] overflow-x-auto overflow-y-hidden px-[30px] pb-[8px] pt-[3px] scrollbar-hide">
          <div className="flex w-max min-w-full flex-nowrap gap-[10px]">
            {orderedParticipants.map((person, index) => {
              const isDeletedPerson = isDeletedAccountUser(person);
              const code = isDeletedPerson ? "" : String(person?.code || "").trim();
              const avatarSrc = resolveParticipantAvatarSrc(person);
              const canOpenParticipant = Boolean(!isDeletedPerson && code && loadingCode == null);
              const activationDateConfirmed =
                activationDateBadgesEnabled && person?.activation_date_status === "confirmed";
              const showIcon = person?.attended
                ? "attended"
                : (
                    person?.intent_confirmed ||
                    person?.intent_status === "confirmed" ||
                    person?.slot_confirm_status === "confirmed" ||
                    (code && code === userStorageCode && intentStatus === "confirmed") ||
                    activationDateConfirmed
                  )
                    ? "intent"
                    : null;

              return (
                <button
                  key={code || person?.user_id || index}
                  type="button"
                  data-premium={Boolean(person?.has_premium_subscription)}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (canOpenParticipant) onOpenParticipant?.(person);
                  }}
                  disabled={!canOpenParticipant}
                  className={`flex shrink-0 flex-col items-center ${canOpenParticipant ? "" : "cursor-default"}`}
                >
                    <div className="relative">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={t("participants_section.avatar_alt", { code })}
                          onError={(event) => {
                            event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                          }}
                          className={`h-[55px] w-[55px] rounded-full object-cover ${
                            person?.has_premium_subscription
                              ? "ring-2 ring-white"
                              : person?.avatar_custom_url || person?.imageUrl
                                ? "ring-2 ring-white/80"
                                : ""
                          }`}
                        />
                      ) : (
                        <div
                          aria-label={DELETED_USER_LABEL}
                          className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
                        >
                          DU
                        </div>
                      )}
                      {showIcon === "attended" ? (
                        <img
                          src="/assets/events/attended_icon.svg"
                          alt=""
                          aria-hidden="true"
                          className="absolute bottom-0 right-0 h-5 w-5"
                        />
                      ) : null}
                      {isHost && showIcon === "intent" ? (
                        <img
                          src="/assets/events/intend_icon.svg"
                          alt=""
                          aria-hidden="true"
                          className="absolute bottom-0 right-0 h-5 w-5"
                        />
                      ) : null}
                      {isHost && showPaymentBadges && person?.host_ticket_paid ? (
                        <span className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-green-500 text-[11px] font-extrabold leading-none text-white">
                          {String(currency || "EUR").toUpperCase() === "RUB" ? "₽" : "€"}
                        </span>
                      ) : null}
                    </div>
                  {isDeletedPerson ? (
                    <span className="mt-[5px] max-w-[82px] truncate text-[11px] font-bold leading-[14px] text-white/75">
                      {DELETED_USER_LABEL}
                    </span>
                  ) : (
                    <PremiumCodeLabel
                      as="div"
                      code={code}
                      isPremium={Boolean(person?.has_premium_subscription)}
                      className="mt-[4px] text-[20px] font-black leading-[22px] text-white"
                      outlineClassName="rounded-[4px] px-[3px] py-0"
                    />
                  )}
                </button>
              );
            })}

            {hasReservedSlots
              ? Array.from({ length: reservedSlotsCount }).map((_, index) => (
                  <div
                    key={`mobile-reserved-new-joiner-${index}`}
                    className="flex shrink-0 flex-col items-center"
                  >
                    <div className="relative">
                      <div className="h-[55px] w-[55px] rounded-full border border-white/35 bg-white/25 shadow-[inset_0_0_18px_rgba(255,255,255,0.16)]" />
                      <img
                        src="/assets/events/lock_icon.svg"
                        alt=""
                        aria-hidden="true"
                        className="absolute bottom-0 right-0 h-5 w-5"
                      />
                    </div>
                    <span className="mt-[4px] text-[20px] font-black leading-[22px] text-white/80">
                      {t("participants_section.lock_reserved_label", { defaultValue: "NEW" })}
                    </span>
                  </div>
                ))
              : null}
          </div>
        </div>
      ) : null}

      {hasReservedSlots ? (
        <p className="mt-[2px] flex flex-wrap items-center gap-x-[5px] gap-y-[2px] text-[11px] font-medium leading-[14px] text-white/68">
          <span className="inline-flex items-center whitespace-nowrap">
            <img
              src="/assets/events/lock_icon.svg"
              alt=""
              aria-hidden="true"
              className="h-4 w-4"
            />
            <span className="ml-[4px] font-semibold text-white/78">{reservedSlotsCount}</span>
          </span>
          <span>{t("participants_section.reserved_seats", { defaultValue: "seats reserved for new joiners" })}</span>
          {unlockBeforeLabel ? (
            <>
              <span aria-hidden="true">•</span>
              <span>
                {t("participants_section.unlocks_label", {
                  label: unlockBeforeLabel,
                  defaultValue: "unlocks {{label}}",
                })}
              </span>
            </>
          ) : null}
        </p>
      ) : null}
    </section>
  );
};

const firstFiniteStat = (...values) => {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const EventStarRow = ({ value }) => {
  const rating = Number(value);
  const rounded = Number.isFinite(rating) ? Math.max(0, Math.min(5, Math.round(rating))) : 0;
  return (
    <span className="inline-flex items-center gap-[2px] text-[14px] leading-none">
      {Array.from({ length: 5 }).map((_, index) => (
        <span key={index} className={index < rounded ? "text-white" : "text-white/35"}>
          ★
        </span>
      ))}
    </span>
  );
};

export const EventHostsSection = ({
  t,
  hostList = [],
  loadingCode,
  getHostAvatarSrc,
  getHostLabel,
  onOpenHost,
}) => {
  if (!hostList.length) return null;

  const multiple = hostList.length > 1;
  const mainHost = hostList[0];
  const mainHostDeleted = isDeletedAccountUser(mainHost);
  const mainHostIsClub = ["club", "platform"].includes(mainHost?.type);
  const clubUserHosts = mainHostIsClub
    ? hostList.filter((host) => !["club", "platform"].includes(host?.type))
    : [];
  const hostedCount = firstFiniteStat(
    mainHost?.hosted_events_count,
    mainHost?.hosted_events,
    mainHost?.events_hosted,
    mainHost?.created_events_count,
    mainHost?.host_created
  );
  const visitedCount = firstFiniteStat(
    mainHost?.visited_events_count,
    mainHost?.visited_events,
    mainHost?.events_visited,
    mainHost?.attended_events_count,
    mainHost?.participant_participated_on
  );
  const rating = firstFiniteStat(
    mainHost?.average_event_rating,
    mainHost?.average_rating,
    mainHost?.host_rating,
    mainHost?.rating
  );

  if (mainHostIsClub && clubUserHosts.length) {
    const clubAvatarSrc = getHostAvatarSrc(mainHost);
    const clubLabel = getHostLabel(mainHost);

    return (
      <section className="mt-[34px]">
        <h2 className="text-[32px] font-medium leading-[34px] text-white">
          {t("events.details.about_host", { defaultValue: "About host:" })}
        </h2>
        <div className="mt-[14px] flex max-w-full flex-col items-start">
          <button
            type="button"
            onClick={() => onOpenHost(mainHost)}
            disabled={mainHost?.type === "platform"}
            className="flex max-w-full items-center gap-[14px] text-left"
          >
            {clubAvatarSrc ? (
              <img
                src={clubAvatarSrc}
                alt=""
                aria-hidden="true"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                }}
                className="h-[55px] w-[55px] shrink-0 rounded-full object-cover ring-2 ring-white/80"
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-[55px] w-[55px] shrink-0 items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
              >
                CL
              </div>
            )}
            <span className="min-w-0 break-words text-[22px] font-black leading-[26px] text-white">
              {clubLabel}
            </span>
          </button>

          <div aria-hidden="true" className="flex h-[32px] w-[55px] items-center justify-center text-[22px] font-extrabold leading-none text-white">
            ×
          </div>

          <div className="-mx-[30px] max-w-[calc(100%+60px)] overflow-x-auto overflow-y-hidden px-[30px] pb-[8px] pt-[3px] scrollbar-hide">
            <div className="flex w-max min-w-full flex-nowrap gap-[10px]">
              {clubUserHosts.map((host) => {
                const userHostDeleted = isDeletedAccountUser(host);
                const userHostAvatarSrc = getHostAvatarSrc(host);

                return (
                  <button
                    key={host.id || host.code || host.label}
                    type="button"
                    onClick={() => {
                      if (!userHostDeleted) onOpenHost(host);
                    }}
                    disabled={userHostDeleted || loadingCode != null}
                    className={`flex shrink-0 flex-col items-center ${userHostDeleted ? "cursor-default" : ""}`}
                  >
                    {userHostAvatarSrc ? (
                      <img
                        src={userHostAvatarSrc}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                        }}
                        className={`h-[55px] w-[55px] rounded-full object-cover ${
                          host?.avatar_custom_url ? "ring-2 ring-white/80" : ""
                        }`}
                      />
                    ) : (
                      <div
                        aria-label={DELETED_USER_LABEL}
                        className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
                      >
                        DU
                      </div>
                    )}
                    {userHostDeleted ? (
                      <span className="mt-[5px] max-w-[82px] truncate text-[11px] font-bold leading-[14px] text-white/75">
                        {DELETED_USER_LABEL}
                      </span>
                    ) : (
                      <PremiumCodeLabel
                        as="div"
                        code={getHostLabel(host)}
                        isPremium={Boolean(host?.has_premium_subscription)}
                        className="mt-[5px] max-w-[82px] truncate text-[20px] font-black leading-[22px] text-white"
                        outlineClassName="rounded-[4px] px-[3px] py-0"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!multiple) {
    if (mainHostIsClub) {
      const hostAvatarSrc = getHostAvatarSrc(mainHost);
      const hostLabel = getHostLabel(mainHost);

      return (
        <section className="mt-[34px]">
          <h2 className="text-[32px] font-medium leading-[34px] text-white">
            {t("events.details.about_host", { defaultValue: "About host:" })}
          </h2>
          <button
            type="button"
            onClick={() => onOpenHost(mainHost)}
            className="mt-[14px] flex max-w-full items-center gap-[14px] text-left"
          >
            {hostAvatarSrc ? (
              <img
                src={hostAvatarSrc}
                alt=""
                aria-hidden="true"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                }}
                className="h-[55px] w-[55px] shrink-0 rounded-full object-cover ring-2 ring-white/80"
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-[55px] w-[55px] shrink-0 items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
              >
                CL
              </div>
            )}
            <span className="min-w-0 truncate text-[22px] font-black leading-[26px] text-white">
              {hostLabel}
            </span>
          </button>
        </section>
      );
    }

    return (
      <section className="mt-[34px]">
        <h2 className="text-[32px] font-medium leading-[34px] text-white">
          {t("events.details.about_host", { defaultValue: "About host:" })}
        </h2>
        <div className="mt-[14px] grid grid-cols-[72px_minmax(0,1fr)] gap-[16px]">
          <button
            type="button"
            onClick={() => {
              if (!mainHostDeleted) onOpenHost(mainHost);
            }}
            disabled={mainHostDeleted || (mainHost.type !== "club" && loadingCode != null)}
            className={`flex flex-col items-center ${mainHostDeleted ? "cursor-default" : ""}`}
          >
            {!getHostAvatarSrc(mainHost) ? (
              <div
                aria-label={DELETED_USER_LABEL}
                className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
              >
                DU
              </div>
            ) : (
              <img
                src={getHostAvatarSrc(mainHost)}
                alt=""
                aria-hidden="true"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                }}
                className={`h-[55px] w-[55px] rounded-full object-cover ${
                  mainHost?.avatar_custom_url || mainHost?.imageUrl ? "ring-2 ring-white/80" : ""
                }`}
              />
            )}
            {mainHostDeleted ? (
              <span className="mt-[5px] max-w-[82px] truncate text-[11px] font-bold leading-[14px] text-white/75">
                {DELETED_USER_LABEL}
              </span>
            ) : (
              <PremiumCodeLabel
                as="div"
                code={getHostLabel(mainHost)}
                isPremium={Boolean(
                  mainHost?.type !== "club" && mainHost?.has_premium_subscription
                )}
                className="mt-[5px] max-w-[82px] truncate text-[20px] font-black leading-[22px] text-white"
                outlineClassName="rounded-[4px] px-[3px] py-0"
              />
            )}
          </button>
          <div className="flex min-w-0 flex-col justify-center gap-[8px] text-[15px] font-semibold leading-[18px] text-white">
            <div>
              Hosted: {mainHostDeleted || hostedCount == null ? "-" : `${hostedCount} events`}
            </div>
            <div>
              Visited: {mainHostDeleted || visitedCount == null ? "-" : `${visitedCount} events`}
            </div>
            <div className="flex items-center gap-[8px]">
              <span>Rating:</span>
              {mainHostDeleted ? <span>-</span> : <EventStarRow value={rating} />}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-[34px]">
      <h2 className="text-[32px] font-medium leading-[34px] text-white">
        {t("events.details.hosts_heading", { defaultValue: "Hosts:" })}
      </h2>
      <div className="-mx-[30px] mt-[18px] overflow-x-auto overflow-y-hidden px-[30px] pb-[8px] pt-[3px] scrollbar-hide">
        <div className="flex w-max min-w-full flex-nowrap gap-[10px]">
          {hostList.map((host) => {
            const isDeletedHost = isDeletedAccountUser(host);
            const hostAvatarSrc = getHostAvatarSrc(host);

            return (
              <button
                key={host.id || host.code || host.label}
                type="button"
                onClick={() => {
                  if (!isDeletedHost) onOpenHost(host);
                }}
                disabled={isDeletedHost || (host.type !== "club" && loadingCode != null)}
                className={`flex shrink-0 flex-col items-center ${isDeletedHost ? "cursor-default" : ""}`}
              >
                {hostAvatarSrc ? (
                  <img
                    src={hostAvatarSrc}
                    alt=""
                    aria-hidden="true"
                    onError={(event) => {
                      event.currentTarget.src = DEFAULT_EVENT_AVATAR_SRC;
                    }}
                    className={`h-[55px] w-[55px] rounded-full object-cover ${
                      host?.avatar_custom_url || host?.imageUrl ? "ring-2 ring-white/80" : ""
                    }`}
                  />
                ) : (
                  <div
                    aria-label={DELETED_USER_LABEL}
                    className="flex h-[55px] w-[55px] items-center justify-center rounded-full bg-white/15 text-[13px] font-black leading-none text-white/70"
                  >
                    DU
                  </div>
                )}
                {isDeletedHost ? (
                  <span className="mt-[5px] max-w-[82px] truncate text-[11px] font-bold leading-[14px] text-white/75">
                    {DELETED_USER_LABEL}
                  </span>
                ) : (
                  <PremiumCodeLabel
                    as="div"
                    code={getHostLabel(host)}
                    isPremium={Boolean(host?.type !== "club" && host?.has_premium_subscription)}
                    className="mt-[5px] max-w-[82px] truncate text-[20px] font-black leading-[22px] text-white"
                    outlineClassName="rounded-[4px] px-[3px] py-0"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * Mobile-only content for EventDetailsFullscreen.
 *
 * ВАЖНО: эта разметка — прямое копирование мобильной ветки из EventDetailsFullscreen
 * без изменения условий / классов / текстов. Всё поведение определяется пропсами.
 */
const EventDetailsMobileContent = ({
  // i18n
  t,
  language,

  // event data
  ev,
  isConcept,
  cardDatetime,
  canAddToCalendar,
  onAddToCalendar = () => {},

  // user state
  isParticipant,
  isHost,
  hasConfirmedAttendance,

  // favourite
  favouriteState,
  toggleFav,

  // sharing / navigation
  linkCopied,
  shareEvent,
  openEventChat,
  openMainHostDirectChat = () => {},
  goToMap,
  goToVault,
  onOpenParticipant,
  onOpenHost = () => {},
  participantTicketing = null,
  ticketCancellationQuote = null,
  ticketCancellationLoading = false,
  ticketCancellationSubmitting = false,
  ticketDisputeSubmitting = false,
  onOpenTicketPayment = () => {},
  onLoadTicketCancellation = () => {},
  onSubmitTicketCancellation = () => {},
  onSubmitTicketDispute = () => {},
  ticketPanelOpenRequest = 0,
  ticketPanelOpenView = "summary",
  onTicketPanelOpenHandled = () => {},
  onOpenTicketPanel = () => {},

  // hosts
  hostList,

  // participants / reserve
  participantsSectionRef,
  loadingCode,
  userStorageCode,
  intentStatus,
  activationDateBadgesEnabled = false,
  reserveUI,
  isFull,
  unlockBeforeLabel,

  // layout
  headerRef,
  bottomSafeSpaceCss,
  compactBottomSpacer = false,

  // chat state
  joiningChat,

  // payment / actions
  showPayButton,
  showIntentButton,
  showAttendanceButton,
  primaryActionType,
  newcomerInterestAction = null,
  confirmCtaLabel,
  prepayWindowT3d,
  ticketPaid,
  joinDisabledByReserve,
  unlockCountdownMs,
  unlockCountdownText,
  unlockText,
  isGuest,
  waitlistCtaVisible = false,
  waitlistCtaLabel = "",
  isInWaitlist = false,
  waitlistUnreadCount = 0,
  moreActionsUnreadCount = 0,
  participantRequestsPendingCount = 0,
  eventToolsOpenRequest = 0,
  eventToolsCloseRequest = 0,
  nativeSwipeBackEnabled = true,

  // handlers
  handleEditEvent,
  handleActivateConcept,
  handleHostConfirmEvent = () => {},
  handleReportEvent,
  handleRepeatEvent,
  handleJoinEvent,
  onJoinPaymentRequired = null,
  onCheckoutCapacityReserved = null,
  handleOpenWaitlist = () => {},
  handleOpenTicketSales = () => {},
  onMoreActionClick = null,
  onPrepareMoreAction = null,
  renderMoreActionView = null,
  hostConfirmationStatus = "pending",
  hostConfirmationRequestedAt = null,

  // state setters (modals / payment)
  setIsConfirmOpen,
  setIsLeaveConfirmOpen,
  onOpenLeaveConfirm = null,
  setIsIntentOpen,
  setIntentVariant,
  setPreJoinPaymentFlag,
  setTicketMode,
  setIsJoinConfirmOpen,

  // close handler for header tap-to-close
  onClose,

  // callback, который родитель использует для refreshDetails()+onEventUpdate()
  onAfterJoinByRequestSuccess,
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [actionDockNode, setActionDockNode] = useState(null);
  const [actionDockHeight, setActionDockHeight] = useState(0);
  const [hasBottomNavigation, setHasBottomNavigation] = useState(false);
  const [bottomNavigationInsetPx, setBottomNavigationInsetPx] = useState(null);
  const canJoinPartySession = canJoinEventSession({
    event: ev,
    isHost,
    isParticipant,
    hasConfirmedAttendance,
  });
  const isParticipantOnly = isParticipantOnlyViewer({ isHost, isParticipant });
  const openPartySession = useCallback((e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    navigate(`/events/${ev.id}/party-session`);
  }, [ev?.id, navigate]);
  const hostConfirmationRequested = Boolean(hostConfirmationRequestedAt);
  const paidTicketTotalAmount = Number.parseFloat(
    participantTicketing?.total_paid_amount || "0"
  );
  const leaveRequiresPaidCancellation = Boolean(
    participantTicketing &&
    Number.isFinite(paidTicketTotalAmount) &&
    paidTicketTotalAmount > 0
  );
  const hasTicketCancellationCase = Boolean(
    participantTicketing?.open_cancellation_request?.id ||
    participantTicketing?.latest_cancellation_request?.id
  );
  const leaveActionLabel = leaveRequiresPaidCancellation
    ? t(
        hasTicketCancellationCase
          ? "events.details.ticket_panel.view_cancellation"
          : "events.details.ticket_panel.cancel_reservation_button"
      )
    : t(isConcept ? "events.details.leave_concept" : "events.details.leave_event");
  const hasParticipantReservation = hasPaidParticipantReservation(participantTicketing);
  const eventChatAction = useMemo(
    () => ({
      id: "event_chat",
      ariaLabel: t(isConcept ? "events.details.concept_chat" : "events.details.event_chat"),
      onClick: (e) => {
        e.stopPropagation();
        openEventChat?.(e);
      },
      iconSrc: EVENT_CHAT_BUTTON_ICON_SRC,
      iconAlt: "",
      iconClassName: "w-6 h-6",
      className: "text-blue-600 bg-white",
      label: t(isConcept ? "events.details.concept_chat" : "events.details.event_chat"),
      disabled: joiningChat,
    }),
    [isConcept, joiningChat, openEventChat, t]
  );
  const fullWidthEventChatAction = useMemo(
    () => ({ ...eventChatAction, forceFull: true }),
    [eventChatAction]
  );
  const participantReservationAction = useMemo(
    () => ({
      id: "participant_reservation",
      ariaLabel: t("events.details.ticket_panel.cta_label"),
      onClick: (e) => {
        e.stopPropagation();
        onOpenTicketPanel("summary");
      },
      iconSrc: null,
      className: "text-blue-600 bg-white",
      label: t("events.details.ticket_panel.cta_label"),
    }),
    [onOpenTicketPanel, t]
  );
  const participantLeaveAction = useMemo(
    () => ({
      id: "participant_leave",
      ariaLabel: leaveActionLabel,
      onClick: (e) => {
        e.stopPropagation();
        if (typeof onOpenLeaveConfirm === "function") {
          onOpenLeaveConfirm(e);
        } else {
          setIsLeaveConfirmOpen(true);
        }
      },
      iconSrc: null,
      className: "text-blue-600 bg-white",
      label: leaveActionLabel,
    }),
    [leaveActionLabel, onOpenLeaveConfirm, setIsLeaveConfirmOpen]
  );
  const participantSessionAction = useMemo(
    () => ({
      id: "participant_session",
      ariaLabel: t("events.details.join_session"),
      onClick: openPartySession,
      iconSrc: "/assets/events/activity_button_icon.svg",
      iconAlt: "",
      iconClassName: "h-6 w-6",
      className: "text-blue-600 bg-white",
      label: t("events.details.join_session_short", {
        defaultValue: t("events.details.join_session"),
      }),
    }),
    [openPartySession, t]
  );
  const confirmedParticipantActions = useMemo(
    () => [
      eventChatAction,
      ...(canJoinPartySession ? [participantSessionAction] : []),
      hasParticipantReservation
        ? participantReservationAction
        : participantLeaveAction,
    ],
    [
      canJoinPartySession,
      eventChatAction,
      hasParticipantReservation,
      participantLeaveAction,
      participantReservationAction,
      participantSessionAction,
    ]
  );
  const canMessageMainHost =
    !isHost && !isParticipant && !!String(ev?.host?.code || "").trim();
  const isAudienceLimitedForViewer = Boolean(
    !isGuest &&
    !isHost &&
    !isParticipant &&
    ev?.social_preference_fit?.is_within_audience === false
  );
  const limitedAudienceNotice = isAudienceLimitedForViewer ? (
    <div className="rounded-[24px] border border-white/20 bg-white/12 px-4 py-3 text-center shadow-[0_12px_28px_rgba(5,10,28,0.18)] backdrop-blur-sm">
      <div className="text-[15px] font-extrabold uppercase tracking-[0.12em] text-white/90">
        {t("events.details.limited_audience.title", { defaultValue: "Limited audience" })}
      </div>
      <p className="mt-2 text-[13px] font-medium leading-[18px] text-white/74">
        {t("events.details.limited_audience.body", {
          defaultValue:
            "This event has a limited audience selected by the host. You can explore other events available for you.",
        })}
      </p>
      <button
        type="button"
        className="mt-3 min-h-[42px] rounded-full bg-white px-5 text-[14px] font-bold text-blue-600"
        onClick={(event) => {
          event.stopPropagation();
          navigate("/events/search");
        }}
      >
        {t("events.details.limited_audience.explore", {
          defaultValue: "Explore available events",
        })}
      </button>
    </div>
  ) : null;
  const eventStatus = String(ev?.status || "").toLowerCase();
  const isExpiredUnvalidated = eventStatus === "expired_unvalidated";
  const isCancelled = eventStatus === "cancelled";
  const isValidationPending = Boolean(
    isHost &&
    ev?.is_validated === false &&
    !["cancelled", "deleted", "finished", "expired_unvalidated"].includes(eventStatus)
  );
  const showHostTicketSales =
    isHost &&
    eventStatus !== "deleted" &&
    !isCancelled &&
    !isExpiredUnvalidated &&
    String(ev?.pricing_model || "").toLowerCase() === "host_paid" &&
    String(ev?.host_payment_collection_method || "").toLowerCase() === "card";
  const [showMoreActions, setShowMoreActions] = useState(false);
  const lastEventToolsOpenRequestRef = useRef(eventToolsOpenRequest);
  const lastEventToolsCloseRequestRef = useRef(eventToolsCloseRequest);
  const renderHostMessageButton = useCallback(() => {
    if (!canMessageMainHost) return null;
    return (
      <button
        type="button"
        onClick={openMainHostDirectChat}
        className={EVENT_DETAILS_SECONDARY_CTA_CLASS}
        aria-label={t("join_section.message_host_aria")}
        title={t("join_section.message_host_button")}
      >
        <img
          src="/assets/events/chat_with_host.svg"
          alt=""
          aria-hidden="true"
          className="h-[24px] w-[24px] object-contain"
        />
      </button>
    );
  }, [canMessageMainHost, openMainHostDirectChat, t]);
  const renderPrimaryWithHostButton = useCallback(
    (primaryNode) => {
      const hostButton = renderHostMessageButton();
      if (!hostButton) return primaryNode;
      return (
        <div className="flex gap-2">
          <div className="flex-1 min-w-0">{primaryNode}</div>
          {hostButton}
        </div>
      );
    },
    [renderHostMessageButton]
  );
  const ageMinRaw = ev?.target_age_min;
  const ageMaxRaw = ev?.target_age_max;
  const hasAgeMin = ageMinRaw !== null && ageMinRaw !== undefined && String(ageMinRaw) !== "";
  const hasAgeMax = ageMaxRaw !== null && ageMaxRaw !== undefined && String(ageMaxRaw) !== "";
  const ageMin = hasAgeMin ? Number(ageMinRaw) : null;
  const ageMax = hasAgeMax ? Number(ageMaxRaw) : null;
  let targetAgeLabel = "";
  if (Number.isFinite(ageMin) && Number.isFinite(ageMax)) {
    targetAgeLabel = `${ageMin}-${ageMax}`;
  } else if (Number.isFinite(ageMin)) {
    targetAgeLabel = `${ageMin}+`;
  } else if (Number.isFinite(ageMax)) {
    targetAgeLabel = `≤${ageMax}`;
  }
  const getHostAvatarSrc = useCallback(
    (host) => resolveUserAvatarSrc(host, DEFAULT_EVENT_AVATAR_SRC),
    []
  );
  const getHostLabel = useCallback(
    (host) => (isDeletedAccountUser(host) ? DELETED_USER_LABEL : host?.label || host?.name || host?.code || ""),
    []
  );

  useEffect(() => {
    setShowMoreActions(false);
  }, [ev?.id]);

  useEffect(() => {
    if (lastEventToolsOpenRequestRef.current === eventToolsOpenRequest) return;
    lastEventToolsOpenRequestRef.current = eventToolsOpenRequest;
    if (!eventToolsOpenRequest) return;
    setShowMoreActions(true);
  }, [eventToolsOpenRequest]);

  useEffect(() => {
    if (lastEventToolsCloseRequestRef.current === eventToolsCloseRequest) return;
    lastEventToolsCloseRequestRef.current = eventToolsCloseRequest;
    if (!eventToolsCloseRequest) return;
    setShowMoreActions(false);
  }, [eventToolsCloseRequest]);

  const openMoreActions = useCallback((e) => {
    e?.stopPropagation?.();
    setShowMoreActions(true);
  }, []);

  const handleNativeSwipeBack = useCallback(() => {
    if (showMoreActions) {
      setShowMoreActions(false);
      return;
    }
    onClose?.();
  }, [onClose, showMoreActions]);

  useNativeSwipeBack(handleNativeSwipeBack, {
    enabled: nativeSwipeBackEnabled,
    priority: 30,
  });

  useLayoutEffect(() => {
    if (!actionDockNode || typeof window === "undefined") return undefined;
    const dock = actionDockNode.querySelector("[data-event-details-action-dock]");
    if (!dock) return undefined;
    let frame = 0;
    const scheduleFit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => fitEventActionFont(dock));
    };
    scheduleFit();
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleFit)
      : null;
    resizeObserver?.observe(actionDockNode);
    const mutationObserver = typeof MutationObserver !== "undefined"
      ? new MutationObserver(scheduleFit)
      : null;
    mutationObserver?.observe(dock, { childList: true, characterData: true, subtree: true });
    window.addEventListener("resize", scheduleFit);
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  }, [actionDockNode]);

  useLayoutEffect(() => {
    if (!actionDockNode || typeof window === "undefined") return undefined;

    const updateHeight = () => {
      const nextHeight = Math.ceil(actionDockNode.getBoundingClientRect().height || 0);
      setActionDockHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    };

    updateHeight();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateHeight)
        : null;
    resizeObserver?.observe(actionDockNode);
    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [actionDockNode]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    if (compactBottomSpacer) {
      setHasBottomNavigation(false);
      setBottomNavigationInsetPx(null);
      return undefined;
    }

    const updatePresence = () => {
      const nav = document.getElementById("bottom-nav");
      const navRect = nav?.getBoundingClientRect?.();
      const dockContainer = actionDockNode?.offsetParent;
      const containerRect = dockContainer?.getBoundingClientRect?.();
      const cssPresent = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue("--bottom-nav-present")
        .trim();
      const nextPresence = Boolean(
        (navRect && navRect.width > 0 && navRect.height > 0) || cssPresent === "1"
      );
      setHasBottomNavigation((currentPresence) =>
        currentPresence === nextPresence ? currentPresence : nextPresence
      );

      const nextInset = nextPresence
        ? resolveActionDockNavigationInsetPx(containerRect, navRect)
        : null;
      setBottomNavigationInsetPx((currentInset) =>
        currentInset === nextInset ? currentInset : nextInset
      );
    };

    updatePresence();
    const mutationObserver = new MutationObserver(updatePresence);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePresence) : null;
    const nav = document.getElementById("bottom-nav");
    if (nav) resizeObserver?.observe(nav);
    if (actionDockNode?.offsetParent) resizeObserver?.observe(actionDockNode.offsetParent);
    const delayedUpdate = window.setTimeout(updatePresence, 350);
    const visualViewport = window.visualViewport;
    window.addEventListener("resize", updatePresence);
    visualViewport?.addEventListener("resize", updatePresence);
    visualViewport?.addEventListener("scroll", updatePresence);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.clearTimeout(delayedUpdate);
      window.removeEventListener("resize", updatePresence);
      visualViewport?.removeEventListener("resize", updatePresence);
      visualViewport?.removeEventListener("scroll", updatePresence);
    };
  }, [actionDockNode, compactBottomSpacer]);

  const CTA_BOTTOM_GAP_PX = 6;
  const CTA_CONTENT_GAP_PX = 24;
  const MORE_ACTIONS_BOTTOM_GAP_PX = 14;
  const navigationSafeSpaceCss =
    !compactBottomSpacer && hasBottomNavigation
      ? Number.isFinite(bottomNavigationInsetPx)
        ? `${bottomNavigationInsetPx}px`
        : "calc(var(--bottom-nav-box-height, var(--bottom-nav-height, 72px)) + max(env(safe-area-inset-bottom, 0px), 10px))"
      : "env(safe-area-inset-bottom, 0px)";
  const actionDockBottomCss = `calc(${navigationSafeSpaceCss} + ${CTA_BOTTOM_GAP_PX}px)`;
  const measuredActionSafeSpaceCss = `calc(${navigationSafeSpaceCss} + ${actionDockHeight}px + ${CTA_BOTTOM_GAP_PX + CTA_CONTENT_GAP_PX}px)`;
  const effectiveBottomSafeSpaceCss = bottomSafeSpaceCss
    ? `max(${bottomSafeSpaceCss}, ${measuredActionSafeSpaceCss})`
    : measuredActionSafeSpaceCss;
  const ctaEndSpacerCss = compactBottomSpacer
    ? measuredActionSafeSpaceCss
    : effectiveBottomSafeSpaceCss;
  const moreActionsEndSpacerCss = compactBottomSpacer
    ? "16px"
    : `calc(var(--bottom-nav-clearance, 112px) + ${MORE_ACTIONS_BOTTOM_GAP_PX}px)`;
  const eventCoverSrc = getEventCoverSrc(ev);
  const {
    palette: eventHeroPalette,
    ready: eventHeroPaletteReady,
  } = useImageGradientPalette(eventCoverSrc, EVENT_HERO_FALLBACK_RGB);
  const eventDetailsBackgroundStyle = eventCoverSrc
    ? {
        backgroundColor: eventHeroPalette.floorCss,
        backgroundImage: "none",
      }
    : EVENT_DETAILS_DEFAULT_BACKGROUND_STYLE;
  if (!ev) return null;
  if (eventCoverSrc && !eventHeroPaletteReady) {
    return <EventDetailsLoadingSkeleton desktop={false} />;
  }
  const isHostlessConcept = Boolean(ev?.is_hostless_concept);
  const eventBoosted = Boolean(ev?.is_promoted_active ?? ev?.promoted);
  const eventIconSrc = String(ev?.icon_image_url || "").trim();
  const conditionChips = buildConditionChips({ ev, t, targetAgeLabel });
  const locationLabel = getLocationLabel(ev, t, language);
  const durationLabel = formatDurationLabel(ev?.approximate_duration, t);
  const dateLabel = formatEventDateTimeLabel(ev, cardDatetime, t);
  const paymentSummary = buildEventPaymentSummary(
    ev,
    t,
    typeof navigator !== "undefined" ? navigator.language : "en"
  );
  const summaryDateLabel = isConcept
    ? t("event_card.concept_label", { defaultValue: "Concept" })
    : formatEventCardDateTimeLabel(ev, cardDatetime, language);
  const topSecondaryButton = handleReportEvent
    ? {
        icon: "/assets/events/report_event_icon.svg",
        label: t("events.details.report_event", { defaultValue: "Report event" }),
        onClick: handleReportEvent,
        disabled: false,
      }
    : null;

  return (
    <div
      className="relative flex h-full min-h-0 flex-col overflow-hidden text-white"
      style={{
        ...eventDetailsBackgroundStyle,
        "--event-details-floor": eventHeroPalette.floorCss,
        "--event-details-scrollbar-track": eventHeroPalette.floorCss,
      }}
    >
      <div
        ref={scrollRef}
        className="event-details-scrollbar relative z-10 min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-none touch-auto [-webkit-overflow-scrolling:touch]"
        style={{
          overscrollBehavior: "none",
          overscrollBehaviorY: "none",
        }}
      >
      <div
        className="relative h-max min-h-full w-full shrink-0"
        style={eventDetailsBackgroundStyle}
      >
        <EventHeroBackdrop coverSrc={eventCoverSrc} title={ev?.name} palette={eventHeroPalette} />
        <HostMoreActionsPanel
          paymentRequest={usesHostPaymentRequest(ev)}
          open={showMoreActions}
          t={t}
          isPrivateEvent={Boolean(
            ev?.join_by_request ||
              Boolean(ev?.skill_access?.enabled) ||
              Number(ev?.participant_requests_pending_total || 0) > 0 ||
              (Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0)
          )}
          isConcept={Boolean(isConcept)}
          hasConceptDatePoll={Boolean(ev?.concept_date_poll?.exists)}
          waitlistUnreadCount={waitlistUnreadCount}
          participantRequestsPendingCount={participantRequestsPendingCount}
          onBack={() => setShowMoreActions(false)}
          onActionClick={onMoreActionClick}
          onPrepareAction={onPrepareMoreAction}
          renderActionView={renderMoreActionView}
          showLifecycleActions={!isHostlessConcept}
          eventBoosted={eventBoosted}
          hostTransferUnavailable={isPaidEventHostTransferUnavailable(ev)}
        />

        {!showMoreActions ? (
          <div className="relative z-10 flex shrink-0 flex-col">
          <header
            ref={headerRef}
            className="px-[30px]"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
          >
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onClose}
                className={EVENT_TOP_ICON_BUTTON_CLASS}
                aria-label={t("event_description_modal.header.close_aria", { defaultValue: "Close" })}
              >
                <img
                  src="/assets/landing/back_arrow.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-[24px] w-[24px]"
                />
              </button>
              <div className="flex items-center gap-[8px]">
                <button
                  type="button"
                  onClick={shareEvent}
                  className={EVENT_TOP_ICON_BUTTON_CLASS}
                  aria-label={t("events.details.share_with_friends", { defaultValue: "Share" })}
                >
                  <img
                    src="/assets/events/share_event_icon.svg"
                    alt=""
                    aria-hidden="true"
                    className="h-[20px] w-[20px]"
                  />
                </button>
                {topSecondaryButton ? (
                  <button
                    type="button"
                    onClick={topSecondaryButton.onClick}
                    disabled={topSecondaryButton.disabled}
                    className={`${EVENT_TOP_ICON_BUTTON_CLASS} ${topSecondaryButton.disabled ? "opacity-60" : ""}`}
                    aria-label={topSecondaryButton.label}
                  >
                    <img
                      src={topSecondaryButton.icon}
                      alt=""
                      aria-hidden="true"
                      className="h-[20px] w-[20px]"
                    />
                  </button>
                ) : null}
              </div>
            </div>
          </header>

          <div className="mt-[51px] px-[26px]">
            <EventDetailsSummaryCard
              ev={ev}
              imageSrc={getEventSummaryImageSrc({
                eventIconSrc,
                badgeLabel: ev?.badge_label,
              })}
              dateLabel={summaryDateLabel}
              isFavourite={favouriteState}
              onToggleFavourite={toggleFav}
              t={t}
            />
            {isValidationPending ? (
              <div className="mt-4">
                <ValidationPendingNotice
                  title={t(
                    isConcept
                      ? "events.details.validation_pending.title_concept"
                      : "events.details.validation_pending.title_event"
                  )}
                  body={t(
                    isConcept
                      ? "events.details.validation_pending.body_concept"
                      : "events.details.validation_pending.body_event"
                  )}
                />
              </div>
            ) : null}
          </div>

          <EventConditionChips chips={conditionChips} />


        </div>
        ) : null}

        <div
          className={`relative z-10 flex-none mv ${
            showMoreActions ? "px-5 pt-6" : "px-[30px] pt-[16px]"
          }`}
          style={{
            // Keep a small native end-padding; the real reserve lives in the spacer below the CTA.
            paddingBottom: "1rem",
            scrollPaddingBottom: effectiveBottomSafeSpaceCss,
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
        {showMoreActions ? (
          <div
            aria-hidden="true"
            className="w-full shrink-0"
            style={{ height: moreActionsEndSpacerCss }}
          />
        ) : (
          <>
            <TranslatableEventDescription
              eventId={ev?.id}
              title={t(
                isConcept
                  ? "events.details.about_concept"
                  : "events.details.about_event"
              )}
              description={ev?.description}
              wrapperClassName="mb-0"
              titleClassName="text-[32px] font-medium leading-[34px] text-white"
              descriptionClassName="mt-[10px] whitespace-pre-line break-words text-[15px] font-normal leading-[18px] text-white [overflow-wrap:anywhere]"
              translateButtonPlacement="inline-after-description"
              translateButtonClassName="ml-[4px] inline-flex h-[18px] min-w-[76px] translate-y-[-1px] items-center justify-center rounded-[18px] bg-[#13C6EE]/30 px-[8px] align-baseline text-[9px] font-medium leading-[11px] text-white"
              translateIconSrc="/assets/events/translation_icon.svg"
              translateIconClassName="mr-[4px] h-[10px] w-[10px]"
            />

            <EventInfoRows
              ev={ev}
              t={t}
              locationLabel={locationLabel}
              durationLabel={durationLabel}
              dateLabel={dateLabel}
              paymentSummary={paymentSummary}
              canAddToCalendar={canAddToCalendar}
              onAddToCalendar={onAddToCalendar}
              goToMap={goToMap}
            />

            <div data-newcomer-tour="gallery">
              <EventPromoGallerySection
                items={Array.isArray(ev?.promo_gallery) ? ev.promo_gallery : []}
                isHost={isHost}
                onEdit={() => handleEditEvent("promo_gallery")}
              />
            </div>

            <div ref={participantsSectionRef} data-newcomer-tour="participants">
              {ev?.is_singles_party && !isHost && !isParticipant ? (
                <SinglesPartyAttendanceSummary event={ev} variant="mobile" />
              ) : (
                <EventPeopleStrip
                  t={t}
                  participants={ev.participants}
                  participantsCount={ev?.participants_count ?? ev?.participants?.length ?? 0}
                  maxParticipants={ev.max_participants}
                  onOpenParticipant={onOpenParticipant}
                  loadingCode={loadingCode}
                  userStorageCode={userStorageCode}
                  intentStatus={intentStatus}
                  activationDateBadgesEnabled={activationDateBadgesEnabled}
                  showPaymentBadges={!!ev?.host_payment_badges_enabled}
                  currency={getEventCurrency(ev)}
                  isHost={isHost}
                  reserveUI={reserveUI}
                  isFull={isFull}
                  unlockBeforeLabel={unlockBeforeLabel}
                />
              )}
            </div>

            <EventBuddyPanel
              eventId={ev?.id}
              buddyFeature={ev?.buddy_feature}
              isParticipant={isParticipant || isHost}
              onOpenVault={goToVault}
              className="mt-[18px]"
            />

            <div data-newcomer-tour="hosts">
              <EventHostsSection
                t={t}
                hostList={hostList}
                loadingCode={loadingCode}
                getHostAvatarSrc={getHostAvatarSrc}
                getHostLabel={getHostLabel}
                onOpenHost={onOpenHost}
              />
            </div>

            {ev.status !== "deleted" &&
            ev.status !== "finished" &&
            hasConfirmedAttendance &&
            isParticipant ? (
              <div className="mt-[18px] flex min-h-[46px] items-center justify-center rounded-[22px] border border-emerald-300/25 bg-emerald-400/15 px-4 text-center text-[15px] font-bold text-emerald-50">
                {t("events.details.attendance_confirmed")}
              </div>
            ) : null}

          {isHostlessConcept ? (
            <HostRewardBlock
              reward={ev?.host_claim_reward || null}
              align="left"
              className={hostList.length > 0 ? "mt-[18px] mb-2.5" : "mt-[30px] mb-2.5"}
              titleClassName="text-[24px] font-bold leading-[28px] text-white"
              showHintToggle={true}
            />
          ) : null}

          {actionDockNode
            ? createPortal(
              <div
                data-event-details-action-dock="true"
                className="pointer-events-auto overflow-visible px-[30px] pb-[2px]"
              >
          {newcomerInterestAction ? (
            <button
              type="button"
              data-event-action-fit="true"
              className={`${EVENT_DETAILS_PRIMARY_CTA_CLASS} mb-[14px]`}
              onClick={newcomerInterestAction.onClick}
            >
              <ChatNotificationBellIcon size={24} className="mr-[8px] shrink-0" />
              <span data-btn-label>{newcomerInterestAction.label}</span>
            </button>
          ) : null}
          {showHostTicketSales ? (
            <button
              type="button"
              data-event-action-fit="true"
              onClick={handleOpenTicketSales}
              className="mb-[14px] flex h-[55px] w-full items-center justify-center rounded-full bg-white text-lg font-semibold text-blue-600"
            >
              <span data-btn-label>{t("events.details.ticket_sales", { defaultValue: "Ticket sales" })}</span>
            </button>
          ) : null}

        {isHost && isParticipant && showAttendanceButton && !hasConfirmedAttendance &&
          ev.status !== "deleted" && ev.status !== "finished" && (
          <button
            type="button"
            data-event-action-fit="true"
            className={`${EVENT_DETAILS_PRIMARY_CTA_CLASS} mb-[14px]`}
            onClick={(e) => {
              e.stopPropagation();
              setIsConfirmOpen(true);
            }}
          >
            <span data-btn-label>{t("events.details.confirm_attendance")}</span>
          </button>
        )}

        {/* Pending — participant (mobile) */}
        {ev.status === "pending" && isParticipantOnly && (
          !hasConfirmedAttendance ? (
            <div className="space-y-2">
              <AdaptiveActionGrid
                items={[
                  ...(showAttendanceButton
                    ? [
                        {
                          id: "pending_confirm",
                          ariaLabel: "Confirm arrival",
                          onClick: (e) => {
                            e.stopPropagation();
                            setIsConfirmOpen(true);
                          },
                          iconSrc: null,
                          className: "text-blue-600 bg-white",
                          label: t("events.details.confirm_attendance"),
                        },
                      ]
                    : []),
                ]}
              />
              {showPayButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = "payment";
                    setIntentVariant(v);
                    setPreJoinPaymentFlag(false); // participant paying for an already reserved spot
                    setTicketMode(true); // "Get Your Ticket"
                    setIsIntentOpen(true);
                  }}
                  className="w-full h-[55px] font-semibold text-lg text-blue-600 bg-white rounded-full"
                >
                  {t("events.details.pay_for_ticket")}
                </button>
              )}
              {hasParticipantReservation ? (
                <ParticipantTicketPanel
                  t={t}
                  ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                  cancellationQuote={ticketCancellationQuote}
                  cancellationLoading={ticketCancellationLoading}
                  cancellationSubmitting={ticketCancellationSubmitting}
                  disputeSubmitting={ticketDisputeSubmitting}
                  onPayNow={onOpenTicketPayment}
                  onLoadCancellation={onLoadTicketCancellation}
                  onSubmitCancellation={onSubmitTicketCancellation}
                  onSubmitDispute={onSubmitTicketDispute}
                  triggerClassName="w-full h-[55px] font-semibold text-lg text-blue-600 bg-white rounded-full"
                  externalOpenToken={ticketPanelOpenRequest}
                  externalOpenView={ticketPanelOpenView}
                  onExternalOpenHandled={onTicketPanelOpenHandled}
                />
              ) : null}
            </div>
          ) : (
            <>
              <AdaptiveActionGrid items={confirmedParticipantActions} />
              {hasParticipantReservation ? (
                <ParticipantTicketPanel
                  t={t}
                  ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                  cancellationQuote={ticketCancellationQuote}
                  cancellationLoading={ticketCancellationLoading}
                  cancellationSubmitting={ticketCancellationSubmitting}
                  disputeSubmitting={ticketDisputeSubmitting}
                  onPayNow={onOpenTicketPayment}
                  onLoadCancellation={onLoadTicketCancellation}
                  onSubmitCancellation={onSubmitTicketCancellation}
                  onSubmitDispute={onSubmitTicketDispute}
                  hideTrigger
                  externalOpenToken={ticketPanelOpenRequest}
                  externalOpenView={ticketPanelOpenView}
                  onExternalOpenHandled={onTicketPanelOpenHandled}
                />
              ) : null}
            </>
          )
        )}

        {/* Finished — participant (mobile) */}
        {ev.status === "finished" && (isParticipant || isHost) && (
          isHost ? (
            <button
              onClick={handleRepeatEvent}
              className="w-full h-[55px] font-semibold text-lg text-blue-600 bg-white rounded-full"
            >
              {t("events.details.repeat_event")}
            </button>
          ) : (
            <div className="space-y-2">
              {hasParticipantReservation ? (
                <ParticipantTicketPanel
                  t={t}
                  ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                  cancellationQuote={ticketCancellationQuote}
                  cancellationLoading={ticketCancellationLoading}
                  cancellationSubmitting={ticketCancellationSubmitting}
                  disputeSubmitting={ticketDisputeSubmitting}
                  onPayNow={onOpenTicketPayment}
                  onLoadCancellation={onLoadTicketCancellation}
                  onSubmitCancellation={onSubmitTicketCancellation}
                  onSubmitDispute={onSubmitTicketDispute}
                  triggerClassName="w-full h-[55px] font-semibold text-lg text-blue-600 bg-white rounded-full"
                  externalOpenToken={ticketPanelOpenRequest}
                  externalOpenView={ticketPanelOpenView}
                  onExternalOpenHandled={onTicketPanelOpenHandled}
                />
              ) : null}
            </div>
          )
        )}

        {isExpiredUnvalidated && isHost && (
          <div className="space-y-3 rounded-[22px] border border-white/14 bg-white/[0.055] p-4">
            <p className="text-center text-sm font-medium leading-5 text-white/75">
              {t("events.details.expired_unvalidated_note")}
            </p>
            <button
              type="button"
              onClick={handleRepeatEvent}
              className="h-[55px] w-full rounded-full bg-white text-lg font-semibold text-blue-600"
            >
              {t("events.details.repeat_event")}
            </button>
          </div>
        )}

        {!showMoreActions && ev.status === "pending" && isConcept && isHost && !canJoinPartySession && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleEditEvent();
            }}
            className="h-[55px] w-full rounded-full bg-white text-lg font-semibold text-blue-600"
          >
            {t("events.details.edit_concept_short")}
          </button>
        )}

        <div>
          {/* Основной CTA-блок (mobile) */}
          {ev.status !== "deleted" &&
            ev.status !== "finished" &&
            (ev.status !== "pending" || (isHost && canJoinPartySession)) &&
            !isExpiredUnvalidated &&
            (isHost || !hasConfirmedAttendance) && (
              <div>
                {isHost ? (
                  isConcept ? (
                  <AdaptiveActionGrid
                    gap="gap-[14px]"
                    forceTwoColumns
                    items={[
                      {
                        id: "concept_edit",
                        ariaLabel: "Edit concept",
                        onClick: (e) => {
                          e.stopPropagation();
                          handleEditEvent();
                        },
                        iconSrc: "/assets/events/edit_event.svg",
                        iconAlt: "Edit icon",
                        iconClassName: "w-5 h-5",
                        className: "text-blue-600 bg-white",
                        label: t("events.details.edit_concept_short"),
                      },
                      {
                        id: "host_more_actions",
                        ariaLabel: "Event tools",
                        onClick: openMoreActions,
                        iconSrc: "/assets/events/more_actions_icon.svg",
                        iconAlt: "Event tools icon",
                        iconClassName: "w-5 h-5",
                        badgeCount: moreActionsUnreadCount,
                        className: "text-blue-600 bg-white",
                        label: t("events.details.more_actions.open_button_short"),
                      },
                      {
                        ...eventChatAction,
                        label: t("events.details.event_chat"),
                      },
                      {
                        id: "concept_activate",
                        ariaLabel: "Activate concept",
                        onClick: (e) => {
                          e.stopPropagation();
                          handleActivateConcept && handleActivateConcept();
                        },
                        iconSrc: "/assets/events/join_icon.svg",
                        iconAlt: "Activate icon",
                        className: "text-blue-600 bg-white",
                        label: t("events.details.activate_concept_short"),
                      },
                    ]}
                  />
                ) : (
                  hostConfirmationStatus !== "confirmed" && hostConfirmationRequested ? (
                    <>
                      <AdaptiveActionGrid
                        gap="gap-[14px]"
                        forceTwoColumns
                        firstColumnWide
                        items={[
                        {
                          id: "host_confirm",
                          ariaLabel: "Confirm hosting",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleHostConfirmEvent(e);
                          },
                          iconSrc: "/assets/events/join_icon.svg",
                          iconAlt: "Confirm icon",
                          className: "text-blue-600 bg-white",
                          label: t("events.details.confirm_hosting_short", {
                            defaultValue: t("events.details.confirm_hosting"),
                          }),
                        },
                        {
                          id: "host_edit",
                          ariaLabel: "Edit event",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleEditEvent();
                          },
                          iconSrc: "/assets/events/edit_event.svg",
                          iconAlt: "Edit icon",
                          iconClassName: "w-5 h-5",
                          className: "text-blue-600 bg-white",
                          label: t("events.details.edit_event_short", {
                            defaultValue: t("events.details.edit_event"),
                          }),
                        },
                        {
                          id: "host_more_actions",
                          ariaLabel: "Event tools",
                          onClick: openMoreActions,
                          iconSrc: "/assets/events/more_actions_icon.svg",
                          iconAlt: "Event tools icon",
                          iconClassName: "w-5 h-5",
                          badgeCount: moreActionsUnreadCount,
                          className: "text-blue-600 bg-white",
                          label: t("events.details.more_actions.open_button_short"),
                        },
                        ...(canJoinPartySession
                          ? [eventChatAction, participantSessionAction]
                          : [{
                              ...fullWidthEventChatAction,
                              forceFull: false,
                              label: t("events.details.event_chat"),
                            }]),
                        ]}
                      />
                    </>
                  ) : (
                    <>
                      <AdaptiveActionGrid
                        gap="gap-[14px]"
                        forceTwoColumns={canJoinPartySession}
                        items={[
                        {
                          id: "host_edit",
                          ariaLabel: "Edit event",
                          onClick: (e) => {
                            e.stopPropagation();
                            handleEditEvent();
                          },
                          iconSrc: "/assets/events/edit_event.svg",
                          iconAlt: "Edit icon",
                          iconClassName: "w-5 h-5",
                          className: "text-blue-600 bg-white",
                          label: t("events.details.edit_event"),
                        },
                        {
                          id: "host_more_actions",
                          ariaLabel: "Event tools",
                          onClick: openMoreActions,
                          iconSrc: "/assets/events/more_actions_icon.svg",
                          iconAlt: "Event tools icon",
                          iconClassName: "w-5 h-5",
                          badgeCount: moreActionsUnreadCount,
                          className: "text-blue-600 bg-white",
                          label: t("events.details.more_actions.open_button"),
                        },
                        ...(canJoinPartySession
                          ? [eventChatAction, participantSessionAction]
                          : [fullWidthEventChatAction]),
                        ]}
                      />
                    </>
                  )
                )
              ) : isParticipant ? (
                <div className="space-y-2">
                  <AdaptiveActionGrid
                    items={[
                      ...(showIntentButton
                        ? [
                            {
                              id: "participant_confirm",
                              ariaLabel: "Confirm",
                              onClick: (e) => {
                                e.stopPropagation();
                                const v = primaryActionType === "activation_date"
                                  ? "activation_date"
                                  : primaryActionType === "slot"
                                  ? "slot"
                                  : (prepayWindowT3d && !ticketPaid ? "payment" : "intent");
                                setIntentVariant(v);
                                setPreJoinPaymentFlag(false); // participant paying to keep spot
                                setTicketMode(false); // classic "keep your spot" flow
                                setIsIntentOpen(true);
                              },
                              iconSrc: null,
                              className: "text-blue-600 bg-white",
                              label: confirmCtaLabel,
                              forceFull: true,
                            },
                          ]
                        : showAttendanceButton
                          ? [
                              {
                                id: "participant_confirm",
                                ariaLabel: "Confirm arrival",
                                onClick: (e) => {
                                  e.stopPropagation();
                                  setIsConfirmOpen(true);
                                },
                                iconSrc: null,
                                className: "text-blue-600 bg-white",
                                label: confirmCtaLabel,
                                forceFull: true,
                              },
                            ]
                          : []),
                      eventChatAction,
                      hasParticipantReservation
                        ? participantReservationAction
                        : participantLeaveAction,
                    ]}
                  />
                  {showPayButton && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const v = "payment";
                        setIntentVariant(v);
                        setPreJoinPaymentFlag(false); // participant paying for existing spot
                        setTicketMode(true); // "Get Your Ticket"
                        setIsIntentOpen(true);
                      }}
                      className="flex gap-2 justify-center items-center w-full text-lg font-semibold text-blue-600 bg-white rounded-full h-[55px]"
                    >
                      <img
                        src="/assets/events/join_icon.svg"
                        alt="Pay icon"
                        className="w-6 h-6"
                      />
                      <span>{t("events.details.pay_for_ticket")}</span>
                    </button>
                  )}
                  {hasParticipantReservation ? (
                    <ParticipantTicketPanel
                      t={t}
                      ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                      cancellationQuote={ticketCancellationQuote}
                      cancellationLoading={ticketCancellationLoading}
                      cancellationSubmitting={ticketCancellationSubmitting}
                      disputeSubmitting={ticketDisputeSubmitting}
                      onPayNow={onOpenTicketPayment}
                      onLoadCancellation={onLoadTicketCancellation}
                      onSubmitCancellation={onSubmitTicketCancellation}
                      onSubmitDispute={onSubmitTicketDispute}
                      hideTrigger
                      externalOpenToken={ticketPanelOpenRequest}
                      externalOpenView={ticketPanelOpenView}
                      onExternalOpenHandled={onTicketPanelOpenHandled}
                    />
                  ) : null}
                </div>
              ) : isAudienceLimitedForViewer ? (
                limitedAudienceNotice
              ) : !isFull ? (
                <div className="space-y-2">
                  {isInWaitlist ? (
                    renderPrimaryWithHostButton(
                      <button
                        className={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                        aria-label="Waitlist"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenWaitlist(e);
                        }}
                      >
                        <JoinPlusIcon />
                        <span>{waitlistCtaLabel || t("events.details.waitlist.view_waitlist")}</span>
                      </button>
                    )
                  ) : shouldUseParticipantRequestFlow(ev) ? (
                    isGuest ? (
                      renderPrimaryWithHostButton(
                        <button
                          className={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                          aria-label={t(isConcept ? "events.details.join_concept" : "events.details.join_event")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoinEvent(e);
                          }}
                        >
                          <JoinPlusIcon />
                          <span>
                            {t(
                              isHostlessConcept
                                ? "events.details.claim_host_cta"
                                : isConcept
                                  ? "events.details.join_concept"
                                  : "events.details.join_event"
                            )}
                          </span>
                        </button>
                      )
                    ) : (
                      <JoinSection
                        event={ev}
                        isParticipant={false}
                        api={api}
                        eventId={ev.id}
                        onJoined={() => {
                          if (onAfterJoinByRequestSuccess) {
                            onAfterJoinByRequestSuccess();
                          }
                        }}
                        onOpenChat={openEventChat}
                        onOpenAfterJoinModal={() => setIsJoinConfirmOpen(true)}
                        onPaymentRequired={onJoinPaymentRequired}
                        onCheckoutCapacityReserved={onCheckoutCapacityReserved}
                        primaryButtonClassName={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                        disabledPrimaryButtonClassName={EVENT_DETAILS_DISABLED_CTA_CLASS}
                        secondaryButtonClassName={EVENT_DETAILS_SECONDARY_CTA_CLASS}
                        secondaryIconClassName="h-[24px] w-[24px] object-contain"
                        primaryPrefix={<JoinPlusIcon />}
                        primaryJoinLabel={t(isConcept ? "events.details.join_concept" : "join_section.request.join_event")}
                        secondaryAction={
                          canMessageMainHost
                            ? {
                                label: t("join_section.message_host_button"),
                                ariaLabel: t("join_section.message_host_aria"),
                                onClick: openMainHostDirectChat,
                                iconSrc: "/assets/events/chat_with_host.svg",
                              }
                            : null
                        }
                      />
                    )
                  ) : (
                    !isGuest && !isInWaitlist && joinDisabledByReserve && waitlistCtaVisible ? (
                      renderPrimaryWithHostButton(
                        <button
                          className={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                          aria-label="Waitlist"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenWaitlist(e);
                          }}
                        >
                          <JoinPlusIcon />
                          <span>
                            {waitlistCtaLabel || t("events.details.waitlist.join_waitlist")}
                          </span>
                        </button>
                      )
                    ) : (
                      renderPrimaryWithHostButton(
                        <button
                          className={
                            joinDisabledByReserve && !isGuest
                              ? EVENT_DETAILS_DISABLED_CTA_CLASS
                              : EVENT_DETAILS_PRIMARY_CTA_CLASS
                          }
                          aria-label={t(isConcept ? "events.details.join_concept" : "events.details.join_event")}
                          onClick={(e) => {
                            e.stopPropagation();

                            // Guests should always be redirected to auth on Join
                            if (isGuest) {
                              handleJoinEvent(e);
                              return;
                            }

                            if (joinDisabledByReserve) {
                              window.dispatchEvent(
                                new CustomEvent("global-info", {
                                  detail: {
                                    message:
                                      unlockCountdownText != null
                                        ? `Reserved for new joiners. Unlock in ${unlockCountdownText}`
                                        : unlockText
                                        ? `Reserved for new joiners. Unlocks at ${unlockText}`
                                        : "Reserved for new joiners",
                                  },
                                })
                              );
                              return;
                            }

                            handleJoinEvent(e);
                          }}
                          disabled={joinDisabledByReserve && !isGuest}
                          title={
                            joinDisabledByReserve && !isGuest
                              ? unlockCountdownText != null
                                ? `Reserved • unlock in ${unlockCountdownText}`
                                : unlockText
                                ? `Reserved • unlocks at ${unlockText}`
                                : "Reserved for new joiners"
                              : undefined
                          }
                          aria-disabled={
                            joinDisabledByReserve && !isGuest ? "true" : "false"
                          }
                        >
                          {joinDisabledByReserve && !isGuest ? null : <JoinPlusIcon />}
                          <span>
                            {joinDisabledByReserve && !isGuest
                              ? unlockCountdownText != null
                                ? `Reserved • unlock in ${unlockCountdownText}`
                                : unlockText
                                ? `Reserved • unlock ${unlockText}`
                                : "Reserved for new joiners"
                              : t(
                                  isHostlessConcept
                                    ? "events.details.claim_host_cta"
                                    : isConcept
                                      ? "events.details.join_concept"
                                      : "events.details.join_event"
                                )}
                          </span>
                        </button>
                      )
                    )
                  )}
                </div>
                ) : shouldUseParticipantRequestFlow(ev) ? (
                  isGuest ? (
                    renderPrimaryWithHostButton(
                      <button
                        className={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                        aria-label={t(isConcept ? "events.details.join_concept" : "events.details.join_event")}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinEvent(e);
                        }}
                      >
                        <JoinPlusIcon />
                        <span>{t(isConcept ? "events.details.join_concept" : "events.details.join_event")}</span>
                      </button>
                    )
                  ) : (
                    <JoinSection
                      event={ev}
                      isParticipant={false}
                      api={api}
                      eventId={ev.id}
                      onJoined={() => {
                        if (onAfterJoinByRequestSuccess) {
                          onAfterJoinByRequestSuccess();
                        }
                      }}
                      onOpenChat={openEventChat}
                      onOpenAfterJoinModal={() => setIsJoinConfirmOpen(true)}
                      onPaymentRequired={onJoinPaymentRequired}
                      onCheckoutCapacityReserved={onCheckoutCapacityReserved}
                      primaryButtonClassName={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                      disabledPrimaryButtonClassName={EVENT_DETAILS_DISABLED_CTA_CLASS}
                      secondaryButtonClassName={EVENT_DETAILS_SECONDARY_CTA_CLASS}
                      secondaryIconClassName="h-[24px] w-[24px] object-contain"
                      primaryPrefix={<JoinPlusIcon />}
                      primaryJoinLabel={t(isConcept ? "events.details.join_concept" : "join_section.request.join_event")}
                      secondaryAction={
                        canMessageMainHost
                          ? {
                              label: t("join_section.message_host_button"),
                              ariaLabel: t("join_section.message_host_aria"),
                              onClick: openMainHostDirectChat,
                              iconSrc: "/assets/events/chat_with_host.svg",
                            }
                          : null
                      }
                    />
                  )
                ) : waitlistCtaVisible ? (
                  renderPrimaryWithHostButton(
                    <button
                      className={EVENT_DETAILS_PRIMARY_CTA_CLASS}
                      aria-label="Waitlist"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenWaitlist(e);
                      }}
                    >
                      <JoinPlusIcon />
                      <span>
                        {waitlistCtaLabel || t("events.details.waitlist.join_waitlist")}
                      </span>
                    </button>
                  )
                ) : null}
              </div>
            )}

          {/* Confirmed participant actions stay compact; status is shown in page content. */}
          {ev.status !== "deleted" &&
            ev.status !== "finished" &&
            ev.status !== "pending" &&
            hasConfirmedAttendance &&
            isParticipantOnly && (
              <div>
                <AdaptiveActionGrid items={confirmedParticipantActions} />
                {hasParticipantReservation ? (
                  <ParticipantTicketPanel
                    t={t}
                    ticketState={participantTicketing}
                  settlementMode={ev?.settlement_mode || participantTicketing?.settlement_mode || "event_hold"}
                    cancellationQuote={ticketCancellationQuote}
                    cancellationLoading={ticketCancellationLoading}
                    cancellationSubmitting={ticketCancellationSubmitting}
                    disputeSubmitting={ticketDisputeSubmitting}
                    onPayNow={onOpenTicketPayment}
                    onLoadCancellation={onLoadTicketCancellation}
                    onSubmitCancellation={onSubmitTicketCancellation}
                    onSubmitDispute={onSubmitTicketDispute}
                    hideTrigger
                    externalOpenToken={ticketPanelOpenRequest}
                    externalOpenView={ticketPanelOpenView}
                    onExternalOpenHandled={onTicketPanelOpenHandled}
                  />
                ) : null}
              </div>
            )}
        </div>
              </div>,
              actionDockNode
            )
            : null}
            <div
              aria-hidden="true"
              className="w-full shrink-0"
              style={{ height: ctaEndSpacerCss }}
            />
          </>
        )}
        </div>
      </div>
      </div>
      <div
        ref={setActionDockNode}
        aria-hidden={showMoreActions ? "true" : undefined}
        className={`pointer-events-none absolute inset-x-0 z-30 ${
          showMoreActions ? "hidden" : ""
        }`}
        style={{ bottom: actionDockBottomCss }}
      />
    </div>
  );
};

export default EventDetailsMobileContent;
